import apiClient from '../api/client';
import { ApiResponse } from '../../types/api.types';

export interface AdminProfile {
  admin_id: number;
  email: string;
  name: string;
  phone: string;
  profile_image: string | null;
  is_active: number;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateAdminProfileRequest {
  full_name?: string;
  mobile_number?: string;
  file?: File | null;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface DashboardStats {
  total_users: number;
  total_businesses: number;
  pending_approvals: number;
  today_transactions: number;
  today_revenue: string;
  failed_transactions: number;
  pending_refunds: number;
  active_businesses: number;
  suspended_businesses: number;
}

export interface DashboardOverview {
  total_users: number;
  total_businesses: number;
  total_transactions: number;
  total_revenue: string;
  weekly_transactions: number;
  weekly_revenue: string;
  monthly_transactions: number;
  monthly_revenue: string;
}

export interface RecentActivity {
  activity_type: 'user' | 'business' | 'transaction' | 'payment';
  activity_name: string;
  entity_name: string;
  activity_status: string;
  activity_date: string;
  entity_id: number;
}

export interface Category {
  category_id: number;
  category_name: string;
  category_slug: string;
  category_icon: string | null;
  category_description: string | null;
  is_active: number;
  display_order: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  business_count?: number;
}

export interface AddCategoryRequest {
  category_name: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  icon?: File;
}

export interface UpdateCategoryRequest {
  category_id: number;
  category_name: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  icon?: File;
}

// ============== Dynamic Category Fields Types ========
export type FieldType = 'text' | 'number' | 'boolean' | 'dropdown' | 'image' | 'currency';

export interface FieldOption {
  option_id: number;
  field_id: number;
  option_value: string;
  display_order: number;
  created_at: string;
}

export interface CategoryField {
  field_id: number;
  category_id: number;
  field_name: string;
  field_slug: string;
  field_type: FieldType;
  is_required: boolean;
  display_order: number;
  validation_rules: any | null;
  default_value: string | null;
  help_text: string | null;
  created_at: string;
  updated_at: string;
  options: FieldOption[];
}

export interface AddCategoryFieldRequest {
  category_id: number;
  field_name: string;
  field_type: FieldType;
  is_required?: boolean;
  display_order?: number;
  validation_rules?: any;
  options?: string[]; // For dropdown fields
}

export interface UpdateCategoryFieldRequest {
  field_id: number;
  category_id: number;
  field_name?: string;
  field_type?: FieldType;
  is_required?: boolean;
  display_order?: number;
  validation_rules?: any;
  options?: string[];
}

export interface AddFieldOptionRequest {
  field_id: number;
  option_value: string;
  display_order?: number;
}

export interface FieldGroup {
  group_id: number;
  category_id: number;
  group_name: string;
  description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  fields?: string; // JSON string of fields
}

export interface CreateFieldGroupRequest {
  category_id: number;
  group_name: string;
  description?: string;
  display_order?: number;
}

export interface UpdateFieldGroupRequest {
  group_id: number;
  category_id?: number;
  group_name?: string;
  description?: string;
  display_order?: number;
}

export interface CustomLayout {
  layout_id: number;
  category_id: number;
  layout_name: string;
  layout_config: any;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface SaveCustomLayoutRequest {
  category_id: number;
  layout_name?: string;
  layout_config: any;
}

// ============== Business Management Types ========
export type BusinessStatus = 'pending_approval' | 'approved' | 'rejected' | 'active' | 'suspended' | 'paused';

export interface Business {
  business_id: number;
  business_name: string;
  owner_name: string;
  business_category_id: number;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  latitude: string;
  longitude: string;
  gst_number: string | null;
  license_number: string | null;
  description: string | null;
  logo_url: string | null;
  status: BusinessStatus;
  approval_date: string | null;
  approved_by_admin_id: number | null;
  rejection_reason: string | null;
  is_verified: number;
  is_featured: number;
  avg_rating: string;
  total_reviews: number;
  is_24_7: number;
  created_at: string;
  updated_at: string;
  business_timings: string | null;
  is_paused: number;
  onboarding_completed: number;
  category_name?: string;
  total_transactions?: number;
  total_revenue?: string;
}

export interface BusinessWithDetails extends Business {
  photos: BusinessPhoto[];
  services_products: BusinessServiceProduct[];
}

export interface BusinessPhoto {
  photo_id: number;
  business_id: number;
  photo_type: 'storefront' | 'interior' | 'logo' | 'other';
  photo_url: string;
  created_at: string;
}

export interface BusinessServiceProduct {
  id: number;
  business_id: number;
  name: string;
  description: string | null;
  price: string;
  image: string | null;
  is_available: number;
  created_at: string;
}

export interface KYCDocument {
  document_id: number;
  business_id: number;
  document_type: 'gst_certificate' | 'id_proof' | 'business_proof';
  document_name: string;
  document_url: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  verified_by_admin_id: number | null;
  verified_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface BusinessListResponse {
  businesses: Business[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_businesses: number;
    per_page: number;
  };
  stats?: {
    total: number;
    pending: number;
    active: number;
    suspended: number;
  };
}

export interface GetBusinessesParams {
  page?: number;
  limit?: number;
  status?: BusinessStatus;
  category_id?: number;
  search?: string;
}

export interface ApproveBusinessRequest {
  business_id: number;
  notes?: string;
}

export interface RejectBusinessRequest {
  business_id: number;
  rejection_reason: string;
}

export interface SuspendBusinessRequest {
  business_id: number;
  suspension_reason: string;
}

export interface VerifyKYCDocumentRequest {
  document_id: number;
  verification_status: 'verified' | 'rejected';
  notes?: string;
}

export interface UpdateBusinessRequest {
  business_id: number;
  name?: string;
  email?: string;
  phone?: string;
  category_id?: number;
  description?: string;
  address_line?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  latitude?: number;
  longitude?: number;
}

export interface ChangeRequest {
  field_name: string;
  current_value?: string;
  requested_change: string;
  reason?: string;
}

export interface RequestChangesRequest {
  business_id: number;
  change_requests: ChangeRequest[];
  general_notes?: string;
}

export interface BusinessPerformance {
  business_id: number;
  period: string;
  total_transactions: number;
  total_revenue: number;
  avg_transaction_value: number;
  unique_customers: number;
  successful_transactions: number;
  failed_transactions: number;
  successful_revenue: number;
  success_rate: number;
}

export interface DuplicateCheckResponse {
  business_id: number;
  check_by: string;
  duplicates: Business[];
  duplicate_count: number;
}

// ============== User Management Types ========
export type UserStatus = 'active' | 'suspended' | 'inactive';

export interface User {
  user_id: number;
  full_name: string;
  email: string;
  mobile_number: string | null;
  profile_image: string | null;
  gender: string | null;
  language: string;
  dob: string | null;
  age: number | null;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  stripe_customer_id: string | null;
  is_verified: number;
  is_active: number;
  is_suspended: number;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  total_transactions?: number;
  total_spent?: string;
  country?: string | null;
  currency_symbol?: string;
  currency_code?: string;
}

export interface UserTransaction {
  transaction_id: number;
  user_id: number;
  business_id: number;
  amount: string;
  status: 'pending' | 'success' | 'failed' | 'refunded' | 'cancelled';
  payment_method: string;
  business_name: string;
  created_at: string;
  currency_symbol?: string;
  currency_code?: string;
}

export interface UserListResponse {
  users: User[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_users: number;
    per_page: number;
  };
  stats?: {
    total: number;
    active: number;
    suspended: number;
    verified: number;
  };
}

export interface UserTransactionsResponse {
  transactions: UserTransaction[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_transactions: number;
    per_page: number;
  };
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  status?: UserStatus;
  search?: string;
}

export interface SuspendUserRequest {
  user_id: number;
  suspension_reason: string;
}

export interface UpdateUserRequest {
  user_id: number;
  full_name?: string;
  email?: string;
  mobile_number?: string;
}

// ============== Payment/Transaction Management Types ========
export type TransactionStatus = 'pending' | 'processing' | 'success' | 'failed' | 'refunded' | 'cancelled';
export type PaymentMethod = 'wallet' | 'card' | 'upi';
export type RefundStatus = 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed';

export interface Transaction {
  transaction_id: string;
  user_id: number;
  business_id: number;
  amount: string;
  status: TransactionStatus;
  payment_method: PaymentMethod;
  user_name?: string;
  business_name?: string;
  created_at: string;
  updated_at: string;
  is_flagged?: number;
  refund_amount?: string;
  refund_status?: string;
  user_currency_symbol?: string;
  user_currency_code?: string;
  business_currency_symbol?: string;
  business_currency_code?: string;
}

export interface TransactionListResponse {
  transactions: Transaction[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_transactions: number;
    per_page: number;
  };
  stats?: {
    total: number;
    success: number;
    pending: number;
    failed: number;
    refunded: number;
    cancelled: number;
  };
}

export interface RefundRequest {
  refund_id: number;
  transaction_id: string;
  user_id: number;
  business_id: number;
  amount: string;
  refund_amount: string;
  refund_reason: string;
  status: RefundStatus;
  requested_by_admin_id?: number;
  approved_by_admin_id?: number;
  rejection_reason?: string;
  user_name?: string;
  business_name?: string;
  created_at: string;
  updated_at: string;
  user_currency_symbol?: string;
  user_currency_code?: string;
  business_currency_symbol?: string;
  business_currency_code?: string;
}

export interface RefundListResponse {
  refunds: RefundRequest[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_refunds: number;
    per_page: number;
  };
}

export interface GetTransactionsParams {
  page?: number;
  limit?: number;
  status?: TransactionStatus;
  payment_method?: PaymentMethod;
  start_date?: string;
  end_date?: string;
  business_id?: number;
  user_id?: number;
}

export interface ProcessRefundRequest {
  transaction_id: string;
  refund_amount?: number;
  refund_reason: string;
}

export interface ApproveRefundRequest {
  refund_id: number;
}

export interface RejectRefundRequest {
  refund_id: number;
  rejection_reason: string;
}

export interface AutoRefundResponse {
  failed_transactions: Transaction[];
  total_count: number;
  auto_process_enabled: boolean;
}

// ============== Wallet & Settlement Management Types ========
export type SettlementStatus = 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed';
export type SettlementType = 'instant' | 'weekly' | 'manual';
export type SettlementDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface Settlement {
  settlement_id: number;
  settlement_number: string;
  business_id: number;
  bank_detail_id: number;
  amount: string;
  settlement_type: SettlementType;
  charges: string;
  net_amount: string;
  status: SettlementStatus;
  admin_id: number | null;
  processed_at: string | null;
  gateway_settlement_id: string | null;
  gateway_response: any | null;
  failure_reason: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  business_name: string;
  business_logo: string | null;
  business_country?: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string | null;
  bank_name: string;
  currency_symbol?: string;
  currency_code?: string;
}

export interface SettlementListResponse {
  settlements: Settlement[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_settlements: number;
    per_page: number;
  };
}

export interface GetSettlementsParams {
  page?: number;
  limit?: number;
  status?: SettlementStatus;
  business_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface ApproveSettlementRequest {
  settlement_id: number;
  notes?: string;
}

export interface RejectSettlementRequest {
  settlement_id: number;
  rejection_reason?: string;
}

export interface HoldSettlementRequest {
  settlement_id: number;
  hold_reason?: string;
  hold_until?: string;
}

export interface ReleaseSettlementRequest {
  settlement_id: number;
  release_notes?: string;
}

export interface InstantSettlementRequest {
  business_id: number;
  amount: number;
  transaction_ids?: string[];
  notes?: string;
}

export interface WeeklySettlementSchedule {
  schedule_id: number;
  business_id: number | null;
  settlement_day: SettlementDay;
  settlement_time: string;
  is_active: number;
  minimum_amount: string;
  created_at: string;
  business_name?: string;
}

export interface WeeklyScheduleListResponse {
  schedules: WeeklySettlementSchedule[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_schedules: number;
    per_page: number;
  };
}

export interface CreateWeeklyScheduleRequest {
  business_id?: number;
  settlement_day: SettlementDay;
  settlement_time: string;
  is_active?: boolean;
  minimum_amount?: number;
}

export interface UpdateWeeklyScheduleRequest {
  schedule_id: number;
  settlement_day?: SettlementDay;
  settlement_time?: string;
  is_active?: boolean;
  minimum_amount?: number;
}

export interface WalletStatistics {
  total_user_wallet_balance: string;
  total_business_wallet_balance: string;
  total_pending_settlements: string;
  pending_settlement_requests: number;
  pending_settlement_amount: string;
  platform_earnings: string;
  refund_liabilities: string;
  total_wallet_balance: string;
  pending_payouts: string;
}

export interface BusinessWallet {
  wallet_id: number;
  owner_id: number;
  user_id: number | null;
  business_id: number | null;
  owner_type: 'business' | 'user';
  current_balance: string;
  pending_balance: string;
  last_calculated_at: string | null;
  created_at: string;
  updated_at: string;
  total_transactions: number;
  total_credits: string;
  total_debits: string;
}

export interface AdjustWalletRequest {
  business_id: number;
  amount: number;
  adjustment_type: 'credit' | 'debit';
  reason: string;
}

// ============== Reports & Analytics Types ========
export interface DailyReport {
  report_date: string;
  total_transactions: number;
  total_revenue: string;
  new_users: number;
  new_businesses: number;
  failed_transactions: number;
}

export interface WeeklyReport {
  total_transactions: number;
  total_revenue: string;
  new_users: number;
  new_businesses: number;
}

export interface MonthlyReport {
  report_month: number;
  report_year: number;
  total_transactions: number;
  total_revenue: string;
  new_users: number;
  new_businesses: number;
}

export interface CustomReportSection {
  report_section: string;
  total_count: number;
  total_amount: string;
}

export interface UserGrowthData {
  period: string;
  new_users: number;
}

export interface RevenueAnalyticsData {
  period: string;
  revenue: string;
  transaction_count: number;
}

export interface RefundReportSummary {
  total_refunds: number;
  total_refund_amount: string;
  pending_refunds: number;
  approved_refunds: number;
  rejected_refunds: number;
}

export interface RefundReportData {
  refunds: RefundRequest[];
  summary: RefundReportSummary;
}

export interface SettlementSummary {
  total_settlements: number;
  total_settlement_amount: string;
  total_net_amount: string;
  pending_settlements: number;
  processing_settlements: number;
  approved_settlements: number;
  rejected_settlements: number;
  instant_settlements: number;
  scheduled_settlements: number;
}

export interface BusinessBreakdown {
  business_id: number;
  business_name: string;
  settlement_count: number;
  total_amount: string;
}

export interface SettlementSummaryReport {
  summary: SettlementSummary;
  business_breakdown: BusinessBreakdown[];
}

export interface BusinessPerformanceReport {
  business_name: string;
  total_transactions: number;
  total_revenue: string;
  avg_transaction_value: string;
  unique_customers: number;
}

export type ReportType = 'daily' | 'weekly' | 'monthly' | 'custom' | 'refund' | 'settlement';
export type ExportFormat = 'pdf' | 'excel' | 'csv' | 'json';
export type GroupByType = 'day' | 'week' | 'month';

export interface GetDailyReportParams {
  date?: string;
}

export interface GetWeeklyReportParams {
  start_date?: string;
  end_date?: string;
}

export interface GetMonthlyReportParams {
  month?: number;
  year?: number;
}

export interface GetCustomReportParams {
  start_date: string;
  end_date: string;
  report_type?: 'transactions' | 'businesses' | 'users' | 'settlements' | 'all';
}

export interface GetUserGrowthParams {
  start_date?: string;
  end_date?: string;
  group_by?: GroupByType;
}

export interface GetRevenueAnalyticsParams {
  start_date?: string;
  end_date?: string;
  group_by?: GroupByType;
}

export interface GetRefundReportParams {
  start_date?: string;
  end_date?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed' | 'all';
  group_by?: GroupByType;
}

export interface GetSettlementSummaryParams {
  start_date?: string;
  end_date?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed' | 'on_hold' | 'all';
  settlement_type?: 'standard' | 'instant' | 'weekly' | 'all';
  group_by?: GroupByType;
}

export interface ExportReportParams {
  report_type: ReportType;
  format?: ExportFormat;
  date?: string;
  start_date?: string;
  end_date?: string;
}

export interface GetBusinessPerformanceReportParams {
  business_id: number;
  start_date?: string;
  end_date?: string;
}

// ============== App Configuration Types ========
export interface PaymentGatewayConfig {
  api_key: string;
  is_active: boolean;
  api_secret: string;
  gateway_name: 'stripe' | 'razorpay' | 'paypal';
  webhook_secret: string;
}

export interface AppSettings {
  setting_id: number;
  commission_rate: string;
  commission_type: 'percentage' | 'fixed';
  minimum_commission: string;
  maximum_commission: string;
  tax_percentage: string;
  apply_tax_on_commission: number;
  minimum_wallet_balance: string;
  emergency_shutdown: number;
  emergency_shutdown_reason: string | null;
  platform_name: string;
  platform_email: string;
  platform_phone: string | null;
  support_email: string;
  support_phone: string | null;
  payment_gateway_config: PaymentGatewayConfig;
  category_visibility: any | null;
  created_at: string;
  updated_at: string;
}

export interface CommissionSettings {
  commission_rate: string;
  commission_type: 'percentage' | 'fixed';
  minimum_commission: string;
  maximum_commission: string;
}

export interface UpdateAppSettingsRequest {
  commission_rate?: number;
  minimum_wallet_balance?: number;
  payment_gateway_config?: any;
  category_visibility?: any;
  emergency_shutdown?: boolean;
  platform_name?: string;
  platform_email?: string;
  platform_phone?: string;
  support_email?: string;
  support_phone?: string;
}

export interface UpdateCommissionSettingsRequest {
  commission_rate?: number;
  platform_commission_percentage?: number;
  commission_type?: 'percentage' | 'fixed';
  minimum_commission?: number;
  maximum_commission?: number;
  tax_percentage?: number;
  apply_tax_on_commission?: boolean;
}

export interface UpdatePaymentGatewayConfigRequest {
  gateway_name: 'stripe' | 'razorpay' | 'paypal';
  api_key?: string;
  api_secret?: string;
  webhook_secret?: string;
  is_active?: boolean;
  config_data?: any;
}

export interface ToggleEmergencyShutdownRequest {
  enabled: boolean;
  reason?: string;
}

export interface EmergencyShutdownResponse {
  emergency_shutdown: number;
  emergency_shutdown_reason: string | null;
}

// ============== Support & Dispute Management Types ========
export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type SupportTicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type SupportTicketType = 'support' | 'technical' | 'refund' | 'payment' | 'other';
export type CreatorType = 'user' | 'business' | 'admin';

export interface SupportTicket {
  ticket_id: number;
  ticket_number: string;
  created_by_id: number;
  created_by_type: CreatorType;
  ticket_type: SupportTicketType;
  subject: string;
  description: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  assigned_to_admin_id: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  creator_name: string;
  creator_email: string;
  creator_phone?: string | null;
  assigned_admin_name: string | null;
}

export interface SupportTicketMessage {
  message_id: number;
  ticket_id: number;
  sender_id: number;
  sender_type: CreatorType;
  message: string;
  attachments: string | null;
  is_internal: number;
  created_at: string;
  admin_name: string | null;
  user_name: string | null;
  business_name: string | null;
}

export interface SupportTicketDetails {
  ticket: SupportTicket;
  messages: SupportTicketMessage[];
}

export interface SupportTicketListResponse {
  tickets: SupportTicket[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_tickets: number;
    per_page: number;
  };
}

export interface GetSupportTicketsParams {
  page?: number;
  limit?: number;
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  type?: SupportTicketType;
}

export interface UpdateSupportTicketStatusRequest {
  ticket_id: number;
  status: SupportTicketStatus;
  notes?: string;
}

export interface AddSupportTicketMessageRequest {
  ticket_id: number;
  message: string;
  is_internal?: boolean;
}

export interface AssignSupportTicketRequest {
  ticket_id: number;
  assigned_to_admin_id: number;
}

export interface SupportStatistics {
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  today_tickets: number;
  pending_disputes: number;
  under_review_disputes: number;
}

export type CallbackType = 'phone' | 'email' | 'in_person' | 'video_call';
export type CallbackStatus = 'scheduled' | 'completed' | 'cancelled' | 'missed';

export interface ScheduledCallback {
  callback_id: number;
  ticket_id: number;
  scheduled_at: string;
  callback_type: CallbackType;
  notes: string | null;
  scheduled_by_admin_id: number;
  assigned_to_admin_id: number;
  status: CallbackStatus;
  completion_notes: string | null;
  completed_at: string | null;
  completed_by_admin_id: number | null;
  created_at: string;
  updated_at: string;
  ticket_number: string;
  ticket_subject: string;
  created_by_type: CreatorType;
  ticket_creator_name: string;
  scheduled_by_admin_name: string;
  assigned_to_admin_name: string;
}

export interface CallbackListResponse {
  callbacks: ScheduledCallback[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_callbacks: number;
    per_page: number;
  };
}

export interface ScheduleCallbackRequest {
  ticket_id: number;
  scheduled_at: string;
  callback_type: CallbackType;
  notes?: string;
  assigned_to_admin_id?: number;
}

export interface UpdateCallbackStatusRequest {
  callback_id: number;
  status: CallbackStatus;
  completion_notes?: string;
}

export type DisputeStatus = 'pending' | 'under_review' | 'resolved' | 'rejected' | 'closed';
export type DisputeType = 'transaction' | 'refund' | 'settlement' | 'other';

export interface Dispute {
  dispute_id: number;
  dispute_number: string;
  created_by_id: number;
  created_by_type: CreatorType;
  dispute_type: DisputeType;
  subject: string;
  description: string;
  status: DisputeStatus;
  priority: SupportTicketPriority;
  related_transaction_id: string | null;
  related_settlement_id: number | null;
  assigned_to_admin_id: number | null;
  resolution: string | null;
  resolution_notes: string | null;
  action_taken: string | null;
  resolved_at: string | null;
  resolved_by_admin_id: number | null;
  created_at: string;
  updated_at: string;
  creator_name: string;
  creator_email: string;
  creator_phone?: string | null;
  assigned_admin_name: string | null;
}

export interface DisputeListResponse {
  disputes: Dispute[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_disputes: number;
    per_page: number;
  };
}

export interface GetDisputesParams {
  page?: number;
  limit?: number;
  status?: DisputeStatus;
  type?: DisputeType;
}

export interface ResolveDisputeRequest {
  dispute_id: number;
  resolution: 'resolved' | 'rejected';
  resolution_notes: string;
  action_taken?: string;
}

export interface GetCallbacksParams {
  page?: number;
  limit?: number;
  status?: CallbackStatus;
  start_date?: string;
  end_date?: string;
}

// ============== Offers & Promotions Types ========
export type OfferType = 'percentage' | 'fixed' | 'buy_one_get_one';
export type OfferStatus = 'active' | 'inactive' | 'expired' | 'all';

export interface Offer {
  offer_id: number;
  business_id: number | null;
  category_id: number | null;
  title: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: string;
  min_purchase_amount: string | null;
  max_discount_amount: string | null;
  valid_from: string;
  valid_until: string;
  is_active: number;
  usage_limit: number | null;
  usage_count: number;
  created_at: string;
  updated_at: string;
  offer_type: OfferType;
  business_name: string | null;
  category_name: string | null;
  total_usage: number;
}

export interface OfferListResponse {
  offers: Offer[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_offers: number;
    per_page: number;
  };
}

export interface CreateOfferRequest {
  business_id?: number | null;
  category_id?: number | null;
  title: string;
  description?: string | null;
  offer_type: OfferType;
  discount_value: number;
  min_purchase_amount?: number | null;
  max_discount_amount?: number | null;
  valid_from: string;
  valid_until: string;
  usage_limit?: number | null;
  is_active?: boolean;
}

export interface UpdateOfferRequest {
  offer_id: number;
  business_id?: number | null;
  category_id?: number | null;
  title?: string;
  description?: string | null;
  offer_type?: OfferType;
  discount_value?: number;
  min_purchase_amount?: number | null;
  max_discount_amount?: number | null;
  valid_from?: string;
  valid_until?: string;
  usage_limit?: number | null;
  is_active?: boolean;
}

export interface ToggleOfferStatusRequest {
  offer_id: number;
  is_active: boolean;
}

export interface AssociateOfferCategoryRequest {
  offer_id: number;
  category_id: number;
}

export interface AssociateOfferBusinessRequest {
  offer_id: number;
  business_id: number;
}

export interface GetOffersParams {
  page?: number;
  limit?: number;
  status?: OfferStatus;
  offer_type?: OfferType;
}

export interface OfferUsageStatistics {
  usage_date: string;
  usage_count: number;
  unique_users: number;
  total_discount: number;
}

export interface Coupon {
  coupon_id: number;
  coupon_code: string;
  offer_id: number | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: string;
  min_purchase_amount: string | null;
  max_discount_amount: string | null;
  max_usage: number | null;
  max_usage_per_user: number | null;
  usage_count: number;
  valid_from: string;
  valid_until: string;
  is_active: number;
  description: string | null;
  created_by_admin_id: number;
  created_at: string;
  updated_at: string;
  offer_title: string | null;
  offer_description: string | null;
  total_usage: number;
  status: 'active' | 'inactive' | 'expired' | 'scheduled';
}

export interface CouponListResponse {
  coupons: Coupon[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_coupons: number;
    per_page: number;
  };
}

export interface CreateCouponRequest {
  coupon_code: string;
  offer_id?: number | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_purchase_amount?: number;
  max_discount_amount?: number;
  max_usage?: number;
  max_usage_per_user?: number;
  valid_from: string;
  valid_until: string;
  is_active?: boolean;
  description?: string | null;
}

export interface UpdateCouponRequest {
  coupon_id: number;
  discount_type?: 'percentage' | 'fixed';
  discount_value?: number;
  min_purchase_amount?: number;
  max_discount_amount?: number;
  max_usage?: number;
  max_usage_per_user?: number;
  valid_from?: string;
  valid_until?: string;
  is_active?: boolean;
  description?: string | null;
}

export interface GetCouponsParams {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive' | 'expired' | 'all';
  search?: string;
}

export interface CouponUsageStatistics {
  coupon_code: string;
  total_usage: number;
  max_usage: number;
  unique_users: number;
  total_discount: number;
  usage_date: string;
}

export interface CouponDetailsResponse {
  coupon: Coupon;
  usage_stats: CouponUsageStatistics[];
}

// ============== Security & Audit Management Types ========
export interface AuditLog {
  log_id: number;
  admin_id: number;
  action: string;
  resource_type: string;
  resource_id: number | null;
  old_values: any | null;
  new_values: any | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  admin_name: string;
  admin_email: string;
}

export interface AuditLogListResponse {
  logs: AuditLog[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_logs: number;
    per_page: number;
  };
}

export interface GetAuditLogsParams {
  page?: number;
  limit?: number;
  admin_id?: number;
  action_type?: string;
  start_date?: string;
  end_date?: string;
}

export interface LoginHistory {
  id: number;
  admin_id: number;
  login_at: string;
  ip_address: string;
  user_agent: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  location: string | null;
  status: 'success' | 'failed';
  created_at: string;
  admin_name: string;
  admin_email: string;
}

export interface LoginHistoryListResponse {
  history: LoginHistory[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };
}

export interface GetLoginHistoryParams {
  page?: number;
  limit?: number;
  admin_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface AdminSession {
  session_id: number;
  admin_id: number;
  token: string;
  ip_address: string;
  user_agent: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  location: string | null;
  last_activity: string;
  expires_at: string;
  status: 'active' | 'expired' | 'revoked';
  created_at: string;
  admin_name: string;
  admin_email: string;
}

export interface AdminSessionListResponse {
  sessions: AdminSession[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_sessions: number;
    per_page: number;
  };
}

export interface GetAdminSessionsParams {
  page?: number;
  limit?: number;
  admin_id?: number;
  status?: 'active' | 'expired' | 'all';
}

export interface RevokeSessionRequest {
  session_id: number;
}

export interface BlockedIP {
  id: number;
  ip_address: string;
  reason: string;
  blocked_by_admin_id: number;
  blocked_at: string;
  unblocked_at: string | null;
  unblocked_by_admin_id: number | null;
  block_duration_days: number | null;
  expires_at: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
  blocked_by_admin_name: string | null;
  unblocked_by_admin_name: string | null;
}

export interface BlockedIPListResponse {
  blocked_ips: BlockedIP[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_ips: number;
    per_page: number;
  };
}

export interface BlockIPRequest {
  ip_address: string;
  reason: string;
  block_duration_days?: number;
}

export interface UnblockIPRequest {
  ip_address: string;
}

export interface SecurityStatistics {
  blocked_ips_count: number;
  active_sessions: number;
  today_failed_attempts: number;
  today_audit_logs: number;
}

export interface FailedLoginAttempt {
  id: number;
  email: string | null;
  ip_address: string;
  user_agent: string | null;
  attempted_at: string;
  failure_reason: string;
  created_at: string;
  attempted_email: string | null;
}

export interface FailedLoginAttemptListResponse {
  attempts: FailedLoginAttempt[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_attempts: number;
    per_page: number;
  };
}

export interface GetFailedLoginAttemptsParams {
  page?: number;
  limit?: number;
  ip_address?: string;
  start_date?: string;
  end_date?: string;
}

// ============== Roles & Permissions Management Types ========
export interface Role {
  role_id: number;
  role_name: string;
  description: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
  assigned_admins_count?: number;
  permissions_count?: number;
}

export interface CreateRoleRequest {
  role_name: string;
  description?: string | null;
  permissions?: number[];
}

export interface UpdateRoleRequest {
  role_id: number;
  role_name?: string;
  description?: string | null;
  is_active?: boolean;
}

export interface Permission {
  permission_id: number;
  permission_name: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'approve' | 'reject' | 'suspend' | 'manage';
  description: string | null;
  created_at: string;
  assigned_roles_count?: number;
}

export interface CreatePermissionRequest {
  permission_name: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'approve' | 'reject' | 'suspend' | 'manage';
  description?: string | null;
}

export interface RolePermission {
  permission_id: number;
  permission_name: string;
  resource: string;
  action: string;
  description: string | null;
  created_at: string;
  assigned_at: string;
}

export interface RolePermissionsResponse {
  role: Role;
  permissions: RolePermission[];
}

export interface AssignPermissionsToRoleRequest {
  role_id: number;
  permission_ids: number[];
}

export interface AssignRoleToAdminRequest {
  target_admin_id: number;
  role_id: number;
}

export interface RemoveRoleFromAdminRequest {
  target_admin_id: number;
  role_id: number;
}

export interface AdminRole {
  role_id: number;
  role_name: string;
  description: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
  assigned_at: string;
  assigned_by_name: string | null;
}

export interface AdminRolesResponse {
  admin: {
    admin_id: number;
    full_name?: string;
    email: string;
  };
  roles: AdminRole[];
}

export interface GetAdminRolesParams {
  admin_id: number;
}

// ============== Fraud Detection Management Types ========
export type FraudType = 'abnormal_pattern' | 'repeated_failures' | 'rapid_payments' | 'amount_mismatch' | 'location_mismatch' | 'other';
export type FraudStatus = 'pending' | 'under_review' | 'resolved' | 'false_positive' | 'confirmed_fraud';
export type FraudSeverity = 'low' | 'medium' | 'high' | 'critical';
export type FraudResolution = 'resolved' | 'false_positive' | 'confirmed_fraud';

export interface FlaggedTransaction {
  transaction_id: string;
  user_id: number;
  business_id: number;
  amount: string;
  status: string;
  payment_method: string;
  fraud_type: FraudType;
  fraud_reason: string;
  severity: FraudSeverity;
  flagged_at: string;
  flagged_by_admin_id: number;
  resolution: FraudResolution | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  resolved_by_admin_id: number | null;
  user_name?: string;
  business_name?: string;
  flagged_by_admin_name?: string;
}

export interface FlaggedTransactionsResponse {
  transactions: FlaggedTransaction[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_transactions: number;
    per_page: number;
  };
}

export interface GetFlaggedTransactionsParams {
  page?: number;
  limit?: number;
  fraud_type?: FraudType;
  status?: FraudStatus;
}

export interface FlagTransactionRequest {
  transaction_id: string;
  fraud_type: FraudType;
  reason: string;
  severity?: FraudSeverity;
}

export interface ResolveFraudCaseRequest {
  transaction_id: string;
  resolution: FraudResolution;
  resolution_notes: string;
  action_taken?: string | null;
}

export interface FraudStatistics {
  overview: {
    total_flagged?: number;
    pending_review?: number;
    confirmed_fraud?: number;
    false_positives?: number;
    resolved?: number;
  };
  by_type: Array<{
    fraud_type: FraudType;
    count: number;
  }>;
}

export interface AutoDetectFraudParams {
  time_range?: '1h' | '24h' | '7d' | '30d';
  auto_flag?: boolean;
}

export interface DetectedFraud {
  transaction_id: string;
  user_id?: number;
  business_id?: number;
  amount?: string;
  detected_at?: string;
  pattern_details?: string;
}

export interface AutoDetectFraudResponse {
  detected_frauds: {
    abnormal_patterns: DetectedFraud[];
    repeated_failures: DetectedFraud[];
    rapid_payments: DetectedFraud[];
    total_detected: number;
  };
  auto_flagged: boolean;
}

export interface FreezeForFraudRequest {
  entity_type: 'business' | 'user';
  entity_id: number;
  reason: string;
  duration_days?: number;
}

// ============== OTP Security Management Types ========
export type OTPRuleType = 'max_attempts' | 'expiry_time' | 'resend_interval' | 'block_duration' | 'ip_whitelist' | 'ip_blacklist';
export type OTPAppliesTo = 'all' | 'user' | 'business' | 'admin';

export interface OTPSecurityRule {
  rule_id: number;
  rule_name: string;
  rule_type: OTPRuleType;
  rule_value: string;
  description: string | null;
  is_active: number;
  applies_to: OTPAppliesTo;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface CreateOTPRuleRequest {
  rule_name: string;
  rule_type: OTPRuleType;
  rule_value: number | string | string[];
  description?: string | null;
  is_active?: boolean;
  applies_to?: OTPAppliesTo;
}

export interface UpdateOTPRuleRequest {
  rule_id: number;
  rule_name?: string;
  rule_type?: OTPRuleType;
  rule_value?: number | string | string[];
  description?: string | null;
  is_active?: boolean;
  applies_to?: OTPAppliesTo;
}

export interface OTPSecurityStatistics {
  total_rules: number;
  active_rules: string;
  inactive_rules: string;
  rule_types_count: number;
  today_failed_attempts: number;
}

export interface FailedOTPAttempt {
  attempt_id: number;
  entity_type: 'user' | 'business' | 'admin';
  entity_id: number;
  phone_number: string | null;
  email: string | null;
  ip_address: string;
  attempted_at: string;
  failure_reason: string;
  created_at: string;
  entity_name?: string;
}

export interface FailedOTPAttemptsResponse {
  attempts: FailedOTPAttempt[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_attempts: number;
    per_page: number;
  };
}

export interface GetFailedOTPAttemptsParams {
  page?: number;
  limit?: number;
  entity_type?: 'user' | 'business' | 'admin';
  start_date?: string;
  end_date?: string;
}

// ============== Notification Management Types ========
export type NotificationType = 'all' | 'payment' | 'business' | 'offer' | 'system';

export interface AdminNotification {
  notification_id: number;
  title: string;
  message: string;
  data: any;
  is_read: number;
  created_at: string;
  read_at: string | null;
}

export interface AdminNotificationListResponse {
  notifications: AdminNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface NotificationStats {
  unread_count: number;
  total_by_type: {
    payment: number;
    business: number;
    offer: number;
    system: number;
  };
}

export interface GetAdminNotificationsParams {
  page?: number;
  limit?: number;
  type?: NotificationType;
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  notification_for: 'All' | 'User' | 'Business';
  user_ids?: number[];
  business_ids?: number[];
}

export interface ScheduleNotificationRequest {
  title: string;
  message: string;
  schedule_at: string;
  notification_for: 'All' | 'User' | 'Business';
}

export interface AdminBroadcastNotification {
  notification_id: number;
  title: string;
  message: string;
  schedule_at: string;
  notification_for: 'All' | 'User' | 'Business';
  status: 'scheduled' | 'sent';
  created_at: string;
  sent_at: string | null;
  sent_count: number;
  failed_count: number;
  notification_type: 'Immediate' | 'Scheduled';
}

export interface UpdateNotificationRequest {
  notification_id: number;
  title: string;
  message: string;
  schedule_at: string;
  notification_for: 'All' | 'User' | 'Business';
}

// ============== Business Documents Management Types ========
export interface BusinessDocument {
  id: number;
  document_name: string;
  document_type: string;
  document_id: string;
  document_url: string;
  created_at: string;
  updated_at: string;
}

export interface BusinessDocumentListResponse {
  documents: BusinessDocument[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_documents: number;
    per_page: number;
  };
}

export interface CreateBusinessDocumentRequest {
  document_name: string;
  document_type: string;
  document_id: string;
  document_url?: string;
}

export interface UpdateBusinessDocumentRequest {
  id: number;
  document_name?: string;
  document_type?: string;
  document_id?: string;
  document_url?: string;
}

export interface GetBusinessDocumentsParams {
  page?: number;
  limit?: number;
  search?: string;
}

// ============== Testimonial Management Types ========
export type TestimonialStatus = 'pending' | 'approved' | 'rejected';

export interface Testimonial {
  id: number;
  name: string;
  text: string;
  rating: number;
  location: string | null;
  profile: string | null;
  category: string | null;
  status: TestimonialStatus;
  verified: boolean | number; // Backend may return 0/1
  featured: boolean | number; // Backend may return 0/1
  helpful: number;
  ip_address: string;
  user_agent: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface TestimonialListResponse {
  testimonials: Testimonial[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    pending: number;
    approved: number;
    featured: number;
    total: number;
  };
}

export interface CreateTestimonialRequest {
  name: string;
  text: string;
  rating?: number;
  location?: string | null;
  profile?: string | null;
  category?: string | null;
  status?: TestimonialStatus;
  verified?: boolean;
  featured?: boolean;
  helpful?: number;
}

export interface UpdateTestimonialRequest {
  id: number;
  name?: string;
  text?: string;
  rating?: number;
  location?: string | null;
  profile?: string | null;
  category?: string | null;
  status?: TestimonialStatus;
  verified?: boolean;
  featured?: boolean;
  helpful?: number;
}

export interface GetTestimonialsParams {
  page?: number;
  limit?: number;
  status?: TestimonialStatus;
  category?: string;
  search?: string;
  featured?: boolean;
}

// ============== Blog Management Types ========
export type BlogStatus = 'draft' | 'published' | 'archived';

export interface Blog {
  id: number;
  title: string;
  description: string;
  content: string;
  category: string;
  author: string;
  image: string | null;
  tags: string[];
  seo_keywords: string[];
  read_time: number;
  status: BlogStatus;
  featured: boolean | number; // Backend may return 0/1
  slug: string;
  meta_title: string | null;
  meta_description: string | null;
  published_by: string | null;
  published_at: string | null;
  views: number;
  likes: number;
  comments: number;
  rating: number;
  ip_address: string;
  user_agent: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface BlogListResponse {
  blogs: Blog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    draft: number;
    published: number;
    featured: number;
    total: number;
  };
}

export interface CreateBlogRequest {
  title: string;
  description: string;
  content: string;
  category: string;
  author: string;
  image?: File | null;
  tags?: string[];
  seoKeywords?: string[];
  readTime?: number;
  status?: BlogStatus;
  featured?: boolean;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  publishedBy?: string;
}

export interface UpdateBlogRequest {
  id: number;
  title?: string;
  description?: string;
  content?: string;
  category?: string;
  author?: string;
  image?: File | null;
  tags?: string[];
  seoKeywords?: string[];
  readTime?: number;
  status?: BlogStatus;
  featured?: boolean;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  publishedBy?: string;
}

export interface GetBlogsParams {
  page?: number;
  limit?: number;
  status?: BlogStatus;
  category?: string;
  search?: string;
  featured?: boolean;
}

// ============== Contact Submission Management Types ========
export type ContactSubmissionStatus = 'pending' | 'in_progress' | 'resolved' | 'closed';

export interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  ip_address: string;
  user_agent: string;
  status: ContactSubmissionStatus;
  is_read: boolean | number; // Backend may return 0/1
  admin_reply: string | null;
  replied_by: string | null;
  replied_at: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ContactSubmissionListResponse {
  submissions: ContactSubmission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    pending: number;
    unread: number;
    total: number;
  };
}

export interface UpdateContactSubmissionStatusRequest {
  id: number;
  status: ContactSubmissionStatus;
  notes?: string | null;
}

export interface GetContactSubmissionsParams {
  page?: number;
  limit?: number;
  status?: ContactSubmissionStatus;
  search?: string;
  is_read?: boolean;
}

// ============== Payment Gateway Options (Admin Bank) Types ========
export interface PaymentGatewayOption {
  payment_gateway_options_id: number;
  payment_method_name: string;
  account_number: string;
  account_holder_name: string;
  bank_name: string;
  routing_number: string | null;
  ifsc_code: string | null;
  swift_code: string | null;
  qr_code: string | null;
  supported_currencies: string[];
  instructions: string | null;
  min_amount: string;
  max_amount: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface AddPaymentGatewayOptionRequest {
  payment_method_name: string;
  account_number: string;
  account_holder_name: string;
  bank_name: string;
  routing_number?: string | null;
  ifsc_code?: string | null;
  swift_code?: string | null;
  qr_code?: string | null;
  supported_currencies?: string[];
  instructions?: string | null;
  min_amount?: number;
  max_amount?: number | null;
  is_active?: boolean;
}

export interface UpdatePaymentGatewayOptionRequest extends Partial<AddPaymentGatewayOptionRequest> {
  payment_gateway_options_id: number;
}

// ============== Admin Bank Account (New) Types ========
export interface AdminBankAccount {
  bank_account_id: number;
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  country_code: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPaymentMethod {
  payment_method_id: number;
  payment_method_name: string;
  display_name: string | null;
  description: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  account_name: string | null;
  account_number: string | null;
  routing_number: string | null;
  qr_code: string | null;
  instructions: string | null;
  supported_currencies: string[] | null;
  minimum_amount: string | number | null;
  maximum_amount: string | number | null;
  status: 'active' | 'inactive';
  is_default: boolean | number;
  sort_order: number;
  enable_for_users: boolean | number;
  allowed_countries: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentMethodRequest {
  payment_method_name: string;
  display_name?: string;
  description?: string;
  username?: string;
  email?: string;
  phone?: string;
  account_name?: string;
  account_number?: string;
  routing_number?: string;
  qr_code?: string;
  instructions?: string;
  supported_currencies?: string[];
  minimum_amount?: number;
  maximum_amount?: number;
  status?: 'active' | 'inactive';
  is_default?: boolean;
  sort_order?: number;
  enable_for_users?: boolean;
  allowed_countries?: string[];
}

export interface UpdatePaymentMethodRequest extends Partial<CreatePaymentMethodRequest> {
  method_id: number;
}

export interface AddAdminBankAccountRequest {
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  country_code: string;
}

export interface UpdateAdminBankAccountRequest {
  bank_account_id: number;
  account_holder_name?: string | null;
  bank_name?: string | null;
  account_number?: string | null;
  country_code?: string | null;
}

// ============== Wallet Request (Deposits & Withdrawals) Types ========
export interface WalletRequest {
  wallet_transaction_id: number;
  wallet_id: number;
  transaction_type: 'credit' | 'debit';
  amount: string;
  balance_before: string;
  balance_after: string;
  reference_type: string;
  reference_id: string | null;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  payment_screenshot: string | null;
  admin_check_status: 'pending' | 'approved' | 'rejected';
  admin_note: string | null;
  payment_gateway_option_id: number | null;
  user_id: number;
  transaction_status: string;
  full_name: string;
  email: string;
  mobile_number: string;
  payment_method_name?: string;
  bank_name?: string;
}

export interface WalletActionRequest {
  transaction_id: number;
  notes?: string;
  reason?: string;
}

class AdminService {

  /**
   * Get Admin Profile
   * GET /admin/profile
   */
  async getProfile(): Promise<AdminProfile> {
    const response = await apiClient.get<ApiResponse<AdminProfile>>('/admin/profile');
    return response.data.data;
  }

  /**
   * Update Admin Profile
   * POST /admin/update-profile (form-data)
   */
  async updateProfile(data: UpdateAdminProfileRequest): Promise<AdminProfile> {
    const formData = new FormData();

    if (data.full_name) {
      formData.append('full_name', data.full_name);
    }

    if (data.mobile_number) {
      formData.append('mobile_number', data.mobile_number);
    }

    if (data.file) {
      formData.append('file', data.file);
    }

    const response = await apiClient.post<ApiResponse<AdminProfile>>(
      '/admin/update-profile',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.data;
  }

  /**
   * Change Admin Password
   * POST /admin/change-password
   */
  async changePassword(data: ChangePasswordRequest): Promise<string> {
    const response = await apiClient.post<ApiResponse<{ admin_id: number; password_updated: boolean }>>(
      '/admin/change-password',
      data
    );
    return response.data.message;
  }

  /**
   * Get Dashboard Stats
   * GET /admin/dashboard/stats
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get<ApiResponse<DashboardStats>>('/admin/dashboard/stats');
    return response.data.data;
  }

  /**
   * Get Dashboard Overview
   * GET /admin/dashboard/overview
   */
  async getDashboardOverview(): Promise<DashboardOverview> {
    const response = await apiClient.get<ApiResponse<DashboardOverview>>('/admin/dashboard/overview');
    return response.data.data;
  }

  /**
   * Get Recent Activities
   * GET /admin/dashboard/recent-activities
   */
  async getRecentActivities(limit: number = 20, type?: string): Promise<RecentActivity[]> {
    const params: any = { limit };
    if (type && type !== 'all') {
      params.type = type;
    }
    const response = await apiClient.get<ApiResponse<RecentActivity[]>>('/admin/dashboard/recent-activities', { params });
    return response.data.data;
  }

  /**
   * Get All Categories
   * GET /admin/get-all-category
   */
  async getAllCategories(): Promise<Category[]> {
    const response = await apiClient.get<ApiResponse<Category[]>>('/admin/get-all-category');
    return response.data.data;
  }

  /**
   * Get Categories with Business Count
   * GET /admin/get-categories-with-business-count
   */
  async getCategoriesWithBusinessCount(): Promise<Category[]> {
    const response = await apiClient.get<ApiResponse<Category[]>>('/admin/get-categories-with-business-count');
    return response.data.data;
  }

  /**
   * Add New Category
   * POST /admin/add-category
   */
  async addCategory(data: AddCategoryRequest): Promise<Category> {
    const formData = new FormData();
    formData.append('category_name', data.category_name);

    if (data.description) {
      formData.append('description', data.description);
    }

    if (data.is_active !== undefined) {
      formData.append('is_active', String(data.is_active));
    }

    if (data.display_order !== undefined) {
      formData.append('display_order', String(data.display_order));
    }

    if (data.icon) {
      formData.append('icon', data.icon);
    }

    const response = await apiClient.post<ApiResponse<Category>>(
      '/admin/add-category',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.data;
  }

  /**
   * Update Category
   * POST /admin/update-category
   */
  async updateCategory(data: UpdateCategoryRequest): Promise<Category> {
    const formData = new FormData();
    formData.append('category_id', String(data.category_id));
    formData.append('category_name', data.category_name);

    if (data.description !== undefined) {
      formData.append('description', data.description);
    }

    if (data.is_active !== undefined) {
      formData.append('is_active', String(data.is_active));
    }

    if (data.display_order !== undefined) {
      formData.append('display_order', String(data.display_order));
    }

    if (data.icon) {
      formData.append('icon', data.icon);
    }

    const response = await apiClient.post<ApiResponse<Category>>(
      '/admin/update-category',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.data;
  }

  /**
   * Delete Category
   * DELETE /admin/delete-category
   */
  async deleteCategory(categoryId: number): Promise<{ category_id: number; category_name: string; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ category_id: number; category_name: string; deleted: boolean }>>(
      '/admin/delete-category',
      {
        data: { category_id: categoryId }
      }
    );
    return response.data.data;
  }

  // ============== Dynamic Category Fields Methods ========
  /**
   * Add Category Field
   * POST /admin/category-fields
   */
  async addCategoryField(data: AddCategoryFieldRequest): Promise<{ field_id: number }> {
    const response = await apiClient.post<ApiResponse<{ field_id: number }>>(
      '/admin/category-fields',
      data
    );
    return response.data.data;
  }

  /**
   * Get Category Fields
   * GET /admin/category-fields?category_id=X
   */
  async getCategoryFields(categoryId: number): Promise<CategoryField[]> {
    const response = await apiClient.get<ApiResponse<CategoryField[]>>(
      '/admin/category-fields',
      {
        params: { category_id: categoryId }
      }
    );
    return response.data.data;
  }

  /**
   * Update Category Field
   * PUT /admin/category-fields
   */
  async updateCategoryField(data: UpdateCategoryFieldRequest): Promise<CategoryField> {
    const response = await apiClient.put<ApiResponse<CategoryField>>(
      '/admin/category-fields',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Category Field
   * DELETE /admin/category-fields?field_id=X
   */
  async deleteCategoryField(fieldId: number): Promise<{ field_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ field_id: number; deleted: boolean }>>(
      '/admin/category-fields',
      {
        params: { field_id: fieldId }
      }
    );
    return response.data.data;
  }

  /**
   * Add Field Option (for dropdown fields)
   * POST /admin/category-fields/options
   */
  async addFieldOption(data: AddFieldOptionRequest): Promise<{ option_id: number }> {
    const response = await apiClient.post<ApiResponse<{ option_id: number }>>(
      '/admin/category-fields/options',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Field Option
   * DELETE /admin/category-fields/options?option_id=X
   */
  async deleteFieldOption(optionId: number): Promise<{ option_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ option_id: number; deleted: boolean }>>(
      '/admin/category-fields/options',
      {
        params: { option_id: optionId }
      }
    );
    return response.data.data;
  }

  /**
   * Create Field Group
   * POST /admin/category-fields/groups
   */
  async createFieldGroup(data: CreateFieldGroupRequest): Promise<{ group_id: number }> {
    const response = await apiClient.post<ApiResponse<{ group_id: number }>>(
      '/admin/category-fields/groups',
      data
    );
    return response.data.data;
  }

  /**
   * Get Field Groups
   * GET /admin/category-fields/groups?category_id=X
   */
  async getFieldGroups(categoryId: number): Promise<FieldGroup[]> {
    const response = await apiClient.get<ApiResponse<FieldGroup[]>>(
      '/admin/category-fields/groups',
      {
        params: { category_id: categoryId }
      }
    );
    return response.data.data;
  }

  /**
   * Update Field Group
   * PUT /admin/category-fields/groups
   */
  async updateFieldGroup(data: UpdateFieldGroupRequest): Promise<FieldGroup> {
    const response = await apiClient.put<ApiResponse<FieldGroup>>(
      '/admin/category-fields/groups',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Field Group
   * DELETE /admin/category-fields/groups?group_id=X
   */
  async deleteFieldGroup(groupId: number): Promise<{ group_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ group_id: number; deleted: boolean }>>(
      '/admin/category-fields/groups',
      {
        params: { group_id: groupId }
      }
    );
    return response.data.data;
  }

  /**
   * Save Custom Layout
   * POST /admin/category-fields/layouts
   */
  async saveCustomLayout(data: SaveCustomLayoutRequest): Promise<{ layout_id: number }> {
    const response = await apiClient.post<ApiResponse<{ layout_id: number }>>(
      '/admin/category-fields/layouts',
      data
    );
    return response.data.data;
  }

  /**
   * Get Custom Layout
   * GET /admin/category-fields/layouts?category_id=X
   */
  async getCustomLayout(categoryId: number): Promise<CustomLayout | null> {
    const response = await apiClient.get<ApiResponse<CustomLayout | null>>(
      '/admin/category-fields/layouts',
      {
        params: { category_id: categoryId }
      }
    );
    return response.data.data;
  }

  // ============== Business Management Methods ========
  /**
   * Get All Businesses
   * GET /admin/businesses
   */
  async getAllBusinesses(params: GetBusinessesParams = {}): Promise<BusinessListResponse> {
    const response = await apiClient.get<ApiResponse<BusinessListResponse>>(
      '/admin/businesses',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Business by ID
   * GET /admin/businesses/:business_id
   */
  async getBusinessById(businessId: number): Promise<BusinessWithDetails> {
    const response = await apiClient.get<ApiResponse<BusinessWithDetails>>(
      `/admin/businesses/${businessId}`
    );
    return response.data.data;
  }

  /**
   * Get Pending Approvals
   * GET /admin/businesses/pending-approvals
   */
  async getPendingApprovals(page: number = 1, limit: number = 20): Promise<BusinessListResponse> {
    const response = await apiClient.get<ApiResponse<BusinessListResponse>>(
      '/admin/businesses/pending-approvals',
      { params: { page, limit } }
    );
    return response.data.data;
  }

  /**
   * Approve Business
   * POST /admin/businesses/approve
   */
  async approveBusiness(data: ApproveBusinessRequest): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/approve',
      data
    );
    return response.data.data;
  }

  /**
   * Reject Business
   * POST /admin/businesses/reject
   */
  async rejectBusiness(data: RejectBusinessRequest): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/reject',
      data
    );
    return response.data.data;
  }

  /**
   * Request Changes from Business
   * POST /admin/businesses/request-changes
   */
  async requestChanges(data: RequestChangesRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/businesses/request-changes',
      data
    );
    return response.data.data;
  }

  /**
   * Suspend Business
   * POST /admin/businesses/suspend
   */
  async suspendBusiness(data: SuspendBusinessRequest): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/suspend',
      data
    );
    return response.data.data;
  }

  /**
   * Unsuspend Business
   * POST /admin/businesses/unsuspend
   */
  async unsuspendBusiness(businessId: number): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/unsuspend',
      { business_id: businessId }
    );
    return response.data.data;
  }

  /**
   * Verify Business
   * POST /admin/businesses/verify
   */
  async verifyBusiness(businessId: number, notes?: string): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/verify',
      { business_id: businessId, verification_notes: notes }
    );
    return response.data.data;
  }

  /**
   * Unverify Business
   * POST /admin/businesses/unverify
   */
  async unverifyBusiness(businessId: number, reason: string): Promise<Business> {
    const response = await apiClient.post<ApiResponse<Business>>(
      '/admin/businesses/unverify',
      { business_id: businessId, reason }
    );
    return response.data.data;
  }

  /**
   * Get Business KYC Documents
   * GET /admin/businesses/kyc-documents
   */
  async getBusinessKYCDocuments(businessId: number): Promise<KYCDocument[]> {
    const response = await apiClient.get<ApiResponse<KYCDocument[]>>(
      '/admin/businesses/kyc-documents',
      { params: { business_id: businessId } }
    );
    return response.data.data;
  }

  /**
   * Verify KYC Document
   * POST /admin/businesses/kyc-documents/verify
   */
  async verifyKYCDocument(data: VerifyKYCDocumentRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/businesses/kyc-documents/verify',
      data
    );
    return response.data.data;
  }

  /**
   * Update Business Details
   * PUT /admin/businesses/update
   */
  async updateBusinessDetails(data: UpdateBusinessRequest): Promise<Business> {
    const response = await apiClient.put<ApiResponse<Business>>(
      '/admin/businesses/update',
      data
    );
    return response.data.data;
  }

  /**
   * Check Duplicate Businesses
   * GET /admin/businesses/check-duplicates
   */
  async checkDuplicateBusinesses(businessId: number, checkBy: string = 'all'): Promise<DuplicateCheckResponse> {
    const response = await apiClient.get<ApiResponse<DuplicateCheckResponse>>(
      '/admin/businesses/check-duplicates',
      { params: { business_id: businessId, check_by: checkBy } }
    );
    return response.data.data;
  }

  /**
   * Get Business Performance
   * GET /admin/businesses/performance
   */
  async getBusinessPerformance(businessId: number, period: string = '30d'): Promise<BusinessPerformance> {
    const response = await apiClient.get<ApiResponse<BusinessPerformance>>(
      '/admin/businesses/performance',
      { params: { business_id: businessId, period } }
    );
    return response.data.data;
  }

  // ============== User Management Methods ========
  /**
   * Get All Users
   * GET /admin/users
   */
  async getAllUsers(params: GetUsersParams = {}): Promise<UserListResponse> {
    const response = await apiClient.get<ApiResponse<UserListResponse>>(
      '/admin/users',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get User by ID
   * GET /admin/users/:user_id
   */
  async getUserById(userId: number): Promise<User> {
    const response = await apiClient.get<ApiResponse<User>>(
      `/admin/users/${userId}`
    );
    return response.data.data;
  }

  /**
   * Get User Transactions
   * GET /admin/users/:user_id/transactions
   */
  async getUserTransactions(
    userId: number,
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<UserTransactionsResponse> {
    const params: any = { page, limit };
    if (status) params.status = status;

    const response = await apiClient.get<ApiResponse<UserTransactionsResponse>>(
      `/admin/users/${userId}/transactions`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Suspend User
   * POST /admin/users/suspend
   */
  async suspendUser(data: SuspendUserRequest): Promise<User> {
    const response = await apiClient.post<ApiResponse<User>>(
      '/admin/users/suspend',
      data
    );
    return response.data.data;
  }

  /**
   * Unsuspend User
   * POST /admin/users/unsuspend
   */
  async unsuspendUser(userId: number): Promise<User> {
    const response = await apiClient.post<ApiResponse<User>>(
      '/admin/users/unsuspend',
      { user_id: userId }
    );
    return response.data.data;
  }

  /**
   * Update User Details
   * PUT /admin/users/update
   */
  async updateUserDetails(data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<ApiResponse<User>>(
      '/admin/users/update',
      data
    );
    return response.data.data;
  }

  // ============== Payment/Transaction Management Methods ========
  /**
   * Get All Transactions
   * GET /admin/transactions
   */
  async getAllTransactions(params: GetTransactionsParams = {}): Promise<TransactionListResponse> {
    const response = await apiClient.get<ApiResponse<TransactionListResponse>>(
      '/admin/transactions',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Transaction by ID
   * GET /admin/transactions/:transaction_id
   */
  async getTransactionById(transactionId: string): Promise<Transaction> {
    const response = await apiClient.get<ApiResponse<Transaction>>(
      `/admin/transactions/${transactionId}`
    );
    return response.data.data;
  }

  /**
   * Get Live Transactions
   * GET /admin/transactions/live
   */
  async getLiveTransactions(limit: number = 50): Promise<Transaction[]> {
    const response = await apiClient.get<ApiResponse<Transaction[]>>(
      '/admin/transactions/live',
      { params: { limit } }
    );
    return response.data.data;
  }

  /**
   * Get Flagged Transactions
   * GET /admin/transactions/flagged
   */
  async getFlaggedTransactions(page: number = 1, limit: number = 20): Promise<TransactionListResponse> {
    const response = await apiClient.get<ApiResponse<TransactionListResponse>>(
      '/admin/transactions/flagged',
      { params: { page, limit } }
    );
    return response.data.data;
  }

  /**
   * Get Auto-Refund Failed Transactions
   * GET /admin/transactions/auto-refund
   */
  async getAutoRefundTransactions(
    timeRange: string = '24h',
    maxAmount?: number,
    autoProcess: boolean = false
  ): Promise<AutoRefundResponse> {
    const response = await apiClient.get<ApiResponse<AutoRefundResponse>>(
      '/admin/transactions/auto-refund',
      {
        params: {
          time_range: timeRange,
          max_amount: maxAmount,
          auto_process: autoProcess
        }
      }
    );
    return response.data.data;
  }

  /**
   * Process Refund
   * POST /admin/transactions/refund
   */
  async processRefund(data: ProcessRefundRequest): Promise<any> {
    const payload = {
      transaction_id: data.transaction_id,
      amount: data.refund_amount,
      refund_reason: data.refund_reason
    };
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/transactions/refund',
      payload
    );
    return response.data.data;
  }

  /**
   * Get Refund Requests
   * GET /admin/refunds
   */
  async getRefundRequests(page: number = 1, limit: number = 20, status?: RefundStatus): Promise<RefundListResponse> {
    const params: any = { page, limit };
    if (status) params.status = status;

    const response = await apiClient.get<ApiResponse<RefundListResponse>>(
      '/admin/refunds',
      { params }
    );
    return response.data.data;
  }

  /**
   * Approve Refund
   * POST /admin/refunds/approve
   */
  async approveRefund(data: ApproveRefundRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/refunds/approve',
      data
    );
    return response.data.data;
  }

  /**
   * Reject Refund
   * POST /admin/refunds/reject
   */
  async rejectRefund(data: RejectRefundRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/refunds/reject',
      data
    );
    return response.data.data;
  }

  // ============== Wallet & Settlement Management Methods ========
  /**
   * Get All Settlements
   * GET /admin/settlements
   */
  async getAllSettlements(params: GetSettlementsParams = {}): Promise<SettlementListResponse> {
    const response = await apiClient.get<ApiResponse<SettlementListResponse>>(
      '/admin/settlements',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Settlement by ID
   * GET /admin/settlements/:settlement_id
   */
  async getSettlementById(settlementId: number): Promise<Settlement> {
    const response = await apiClient.get<ApiResponse<Settlement>>(
      `/admin/settlements/${settlementId}`
    );
    return response.data.data;
  }

  /**
   * Approve Settlement
   * POST /admin/settlements/approve
   */
  async approveSettlement(data: ApproveSettlementRequest): Promise<Settlement> {
    const response = await apiClient.post<ApiResponse<Settlement>>(
      '/admin/settlements/approve',
      data
    );
    return response.data.data;
  }

  /**
   * Reject Settlement
   * POST /admin/settlements/reject
   */
  async rejectSettlement(data: RejectSettlementRequest): Promise<Settlement> {
    const response = await apiClient.post<ApiResponse<Settlement>>(
      '/admin/settlements/reject',
      data
    );
    return response.data.data;
  }

  /**
   * Hold Settlement
   * POST /admin/settlements/hold
   */
  async holdSettlement(data: HoldSettlementRequest): Promise<Settlement> {
    const response = await apiClient.post<ApiResponse<Settlement>>(
      '/admin/settlements/hold',
      data
    );
    return response.data.data;
  }

  /**
   * Release Settlement
   * POST /admin/settlements/release
   */
  async releaseSettlement(data: ReleaseSettlementRequest): Promise<Settlement> {
    const response = await apiClient.post<ApiResponse<Settlement>>(
      '/admin/settlements/release',
      data
    );
    return response.data.data;
  }

  /**
   * Process Instant Settlement
   * POST /admin/settlements/instant
   */
  async processInstantSettlement(data: InstantSettlementRequest): Promise<{ settlement_id: number; amount: number }> {
    const response = await apiClient.post<ApiResponse<{ settlement_id: number; amount: number }>>(
      '/admin/settlements/instant',
      data
    );
    return response.data.data;
  }

  /**
   * Get Weekly Settlement Schedules
   * GET /admin/settlements/weekly-schedule
   */
  async getWeeklySettlementSchedules(params: { page?: number; limit?: number; business_id?: number; is_active?: boolean } = {}): Promise<WeeklyScheduleListResponse> {
    const response = await apiClient.get<ApiResponse<WeeklyScheduleListResponse>>(
      '/admin/settlements/weekly-schedule',
      { params }
    );
    return response.data.data;
  }

  /**
   * Create Weekly Settlement Schedule
   * POST /admin/settlements/weekly-schedule
   */
  async createWeeklySettlementSchedule(data: CreateWeeklyScheduleRequest): Promise<{ schedule_id: number }> {
    const response = await apiClient.post<ApiResponse<{ schedule_id: number }>>(
      '/admin/settlements/weekly-schedule',
      data
    );
    return response.data.data;
  }

  /**
   * Update Weekly Settlement Schedule
   * PUT /admin/settlements/weekly-schedule
   */
  async updateWeeklySettlementSchedule(data: UpdateWeeklyScheduleRequest): Promise<WeeklySettlementSchedule> {
    const response = await apiClient.put<ApiResponse<WeeklySettlementSchedule>>(
      '/admin/settlements/weekly-schedule',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Weekly Settlement Schedule
   * DELETE /admin/settlements/weekly-schedule
   */
  async deleteWeeklySettlementSchedule(scheduleId: number): Promise<{ schedule_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ schedule_id: number; deleted: boolean }>>(
      '/admin/settlements/weekly-schedule',
      { data: { schedule_id: scheduleId } }
    );
    return response.data.data;
  }

  /**
   * Get Wallet Statistics
   * GET /admin/wallet/statistics
   */
  async getWalletStatistics(): Promise<WalletStatistics> {
    const response = await apiClient.get<ApiResponse<WalletStatistics>>(
      '/admin/wallet/statistics'
    );
    return response.data.data;
  }

  /**
   * Get Business Wallet Details
   * GET /admin/wallet/business/:business_id
   */
  async getBusinessWalletDetails(businessId: number): Promise<BusinessWallet> {
    const response = await apiClient.get<ApiResponse<BusinessWallet>>(
      `/admin/wallet/business/${businessId}`
    );
    return response.data.data;
  }

  /**
   * Adjust Wallet Balance
   * POST /admin/wallet/adjust
   */
  async adjustWalletBalance(data: AdjustWalletRequest): Promise<{ business_id: number; adjustment_type: string; amount: number; updated_wallet: BusinessWallet }> {
    const response = await apiClient.post<ApiResponse<{ business_id: number; adjustment_type: string; amount: number; updated_wallet: BusinessWallet }>>(
      '/admin/wallet/adjust',
      data
    );
    return response.data.data;
  }

  // ============== Payment Gateway Options (Admin Bank) Methods ========
  /**
   * Get All Payment Gateway Options
   * GET /admin/payment-gateway-options
   */
  async getPaymentGatewayOptions(): Promise<PaymentGatewayOption[]> {
    const response = await apiClient.get<ApiResponse<PaymentGatewayOption[]>>(
      '/admin/payment-gateway-options'
    );
    return response.data.data;
  }

  /**
   * Add Payment Gateway Option
   * POST /admin/payment-gateway-options
   */
  async addPaymentGatewayOption(data: AddPaymentGatewayOptionRequest): Promise<PaymentGatewayOption> {
    const response = await apiClient.post<ApiResponse<PaymentGatewayOption>>(
      '/admin/payment-gateway-options',
      data
    );
    return response.data.data;
  }

  /**
   * Update Payment Gateway Option
   * PUT /admin/payment-gateway-options
   */
  async updatePaymentGatewayOption(data: UpdatePaymentGatewayOptionRequest): Promise<PaymentGatewayOption> {
    const response = await apiClient.put<ApiResponse<PaymentGatewayOption>>(
      '/admin/payment-gateway-options',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Payment Gateway Option
   * DELETE /admin/payment-gateway-options
   */
  async deletePaymentGatewayOption(payment_gateway_options_id: number): Promise<{ payment_gateway_options_id: number }> {
    const response = await apiClient.delete<ApiResponse<{ payment_gateway_options_id: number }>>(
      '/admin/payment-gateway-options',
      { data: { payment_gateway_options_id } }
    );
    return response.data.data;
  }

  // ============== Wallet Request (Deposits & Withdrawals) Methods ========
  /**
   * Get Pending Deposits
   * GET /admin/wallet/requests/deposits
   */
  async getPendingDeposits(): Promise<WalletRequest[]> {
    const response = await apiClient.get<ApiResponse<WalletRequest[]>>(
      '/admin/wallet/requests/deposits'
    );
    return response.data.data;
  }

  /**
   * Get Pending Withdrawals
   * GET /admin/wallet/requests/withdrawals
   */
  async getPendingWithdrawals(): Promise<WalletRequest[]> {
    const response = await apiClient.get<ApiResponse<WalletRequest[]>>(
      '/admin/wallet/requests/withdrawals'
    );
    return response.data.data;
  }

  /**
   * Approve Request
   * POST /admin/wallet/requests/approve
   */
  async approveWalletRequest(data: WalletActionRequest): Promise<WalletRequest> {
    const response = await apiClient.post<ApiResponse<WalletRequest>>(
      '/admin/wallet/requests/approve',
      data
    );
    return response.data.data;
  }

  /**
   * Reject Request
   * POST /admin/wallet/requests/reject
   */
  async rejectWalletRequest(data: WalletActionRequest): Promise<WalletRequest> {
    const response = await apiClient.post<ApiResponse<WalletRequest>>(
      '/admin/wallet/requests/reject',
      data
    );
    return response.data.data;
  }

  // ============== Reports & Analytics Methods ========
  /**
   * Get Daily Report
   * GET /admin/reports/daily
   */
  async getDailyReport(params: GetDailyReportParams = {}): Promise<DailyReport> {
    const response = await apiClient.get<ApiResponse<DailyReport>>(
      '/admin/reports/daily',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Weekly Report
   * GET /admin/reports/weekly
   */
  async getWeeklyReport(params: GetWeeklyReportParams = {}): Promise<WeeklyReport> {
    const response = await apiClient.get<ApiResponse<WeeklyReport>>(
      '/admin/reports/weekly',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Monthly Report
   * GET /admin/reports/monthly
   */
  async getMonthlyReport(params: GetMonthlyReportParams = {}): Promise<MonthlyReport> {
    const response = await apiClient.get<ApiResponse<MonthlyReport>>(
      '/admin/reports/monthly',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Custom Report
   * GET /admin/reports/custom
   */
  async getCustomReport(params: GetCustomReportParams): Promise<CustomReportSection[]> {
    const response = await apiClient.get<ApiResponse<CustomReportSection[]>>(
      '/admin/reports/custom',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get User Growth Report
   * GET /admin/reports/user-growth
   */
  async getUserGrowthReport(params: GetUserGrowthParams = {}): Promise<UserGrowthData[]> {
    const response = await apiClient.get<ApiResponse<UserGrowthData[]>>(
      '/admin/reports/user-growth',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Revenue Analytics
   * GET /admin/reports/revenue-analytics
   */
  async getRevenueAnalytics(params: GetRevenueAnalyticsParams = {}): Promise<RevenueAnalyticsData[]> {
    const response = await apiClient.get<ApiResponse<RevenueAnalyticsData[]>>(
      '/admin/reports/revenue-analytics',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Refund Report
   * GET /admin/reports/refund
   */
  async getRefundReport(params: GetRefundReportParams = {}): Promise<RefundReportData> {
    const response = await apiClient.get<ApiResponse<RefundReportData>>(
      '/admin/reports/refund',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Settlement Summary Report
   * GET /admin/reports/settlement-summary
   */
  async getSettlementSummary(params: GetSettlementSummaryParams = {}): Promise<SettlementSummaryReport> {
    const response = await apiClient.get<ApiResponse<SettlementSummaryReport>>(
      '/admin/reports/settlement-summary',
      { params }
    );
    return response.data.data;
  }
  /**
   * Get Business Performance Report
   * GET /admin/reports/business/:business_id
   */
  async getBusinessPerformanceReport(params: { business_id: number; start_date: string; end_date: string }): Promise<BusinessPerformanceReport> {
    const { business_id, ...queryParams } = params;
    const response = await apiClient.get<ApiResponse<BusinessPerformanceReport>>(
      `/admin/reports/business/${business_id}`,
      { params: queryParams }
    );
    return response.data.data;
  }

  /**
   * Export Report
   * GET /admin/reports/export
   */
  async exportReport(params: ExportReportParams): Promise<any> {
    const response = await apiClient.get(
      '/admin/reports/export',
      {
        params,
        responseType: params.format === 'pdf' || params.format === 'excel' ? 'blob' : 'text'
      }
    );
    return response.data;
  }

  // ============== App Configuration Methods ========
  /**
   * Get App Settings
   * GET /admin/settings
   */
  async getAppSettings(): Promise<AppSettings> {
    const response = await apiClient.get<ApiResponse<AppSettings>>('/admin/settings');
    return response.data.data;
  }

  /**
   * Update App Settings
   * PUT /admin/settings
   */
  async updateAppSettings(data: UpdateAppSettingsRequest): Promise<AppSettings> {
    const response = await apiClient.put<ApiResponse<AppSettings>>(
      '/admin/settings',
      data
    );
    return response.data.data;
  }

  /**
   * Get Commission Settings
   * GET /admin/settings/commission
   */
  async getCommissionSettings(): Promise<CommissionSettings> {
    const response = await apiClient.get<ApiResponse<CommissionSettings>>('/admin/settings/commission');
    return response.data.data;
  }

  /**
   * Update Commission Settings
   * PUT /admin/settings/commission
   */
  async updateCommissionSettings(data: UpdateCommissionSettingsRequest): Promise<CommissionSettings> {
    const response = await apiClient.put<ApiResponse<CommissionSettings>>(
      '/admin/settings/commission',
      data
    );
    return response.data.data;
  }

  /**
   * Get Payment Gateway Config
   * GET /admin/settings/payment-gateway
   */
  async getPaymentGatewayConfig(): Promise<PaymentGatewayConfig> {
    const response = await apiClient.get<ApiResponse<PaymentGatewayConfig>>('/admin/settings/payment-gateway');
    return response.data.data;
  }

  /**
   * Update Payment Gateway Config
   * PUT /admin/settings/payment-gateway
   */
  async updatePaymentGatewayConfig(data: UpdatePaymentGatewayConfigRequest): Promise<PaymentGatewayConfig> {
    const response = await apiClient.put<ApiResponse<PaymentGatewayConfig>>(
      '/admin/settings/payment-gateway',
      data
    );
    return response.data.data;
  }

  /**
   * Toggle Emergency Shutdown
   * POST /admin/settings/emergency-shutdown
   */
  async toggleEmergencyShutdown(data: ToggleEmergencyShutdownRequest): Promise<EmergencyShutdownResponse> {
    const response = await apiClient.post<ApiResponse<EmergencyShutdownResponse>>(
      '/admin/settings/emergency-shutdown',
      data
    );
    return response.data.data;
  }

  // ============== Support & Dispute Management Methods ========
  /**
   * Get All Support Tickets
   * GET /admin/support/tickets
   */
  async getAllSupportTickets(params: GetSupportTicketsParams = {}): Promise<SupportTicketListResponse> {
    const response = await apiClient.get<ApiResponse<SupportTicketListResponse>>(
      '/admin/support/tickets',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Support Ticket by ID
   * GET /admin/support/tickets/:ticket_id
   */
  async getSupportTicketById(ticketId: number): Promise<SupportTicketDetails> {
    const response = await apiClient.get<ApiResponse<SupportTicketDetails>>(
      `/admin/support/tickets/${ticketId}`
    );
    return response.data.data;
  }

  /**
   * Update Support Ticket Status
   * PUT /admin/support/tickets/status
   */
  async updateSupportTicketStatus(data: UpdateSupportTicketStatusRequest): Promise<SupportTicket> {
    const response = await apiClient.put<ApiResponse<SupportTicket>>(
      '/admin/support/tickets/status',
      data
    );
    return response.data.data;
  }

  /**
   * Add Support Ticket Message
   * POST /admin/support/tickets/message
   */
  async addSupportTicketMessage(data: AddSupportTicketMessageRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/support/tickets/message',
      data
    );
    return response.data.data;
  }

  /**
   * Assign Support Ticket
   * POST /admin/support/tickets/assign
   */
  async assignSupportTicket(data: AssignSupportTicketRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/support/tickets/assign',
      data
    );
    return response.data.data;
  }

  /**
   * Get Support Statistics
   * GET /admin/support/statistics
   */
  async getSupportStatistics(): Promise<SupportStatistics> {
    const response = await apiClient.get<ApiResponse<SupportStatistics>>('/admin/support/statistics');
    return response.data.data;
  }

  /**
   * Get Scheduled Callbacks
   * GET /admin/support/callbacks
   */
  async getScheduledCallbacks(params: GetCallbacksParams = {}): Promise<CallbackListResponse> {
    const response = await apiClient.get<ApiResponse<CallbackListResponse>>(
      '/admin/support/callbacks',
      { params }
    );
    return response.data.data;
  }

  /**
   * Schedule Callback
   * POST /admin/support/callbacks
   */
  async scheduleCallback(data: ScheduleCallbackRequest): Promise<{ callback_id: number }> {
    const response = await apiClient.post<ApiResponse<{ callback_id: number }>>(
      '/admin/support/callbacks',
      data
    );
    return response.data.data;
  }

  /**
   * Update Callback Status
   * PUT /admin/support/callbacks/status
   */
  async updateCallbackStatus(data: UpdateCallbackStatusRequest): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(
      '/admin/support/callbacks/status',
      data
    );
    return response.data.data;
  }

  /**
   * Get All Disputes
   * GET /admin/disputes
   */
  async getAllDisputes(params: GetDisputesParams = {}): Promise<DisputeListResponse> {
    const response = await apiClient.get<ApiResponse<DisputeListResponse>>(
      '/admin/disputes',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Dispute by ID
   * GET /admin/disputes/:dispute_id
   */
  async getDisputeById(disputeId: number): Promise<Dispute> {
    const response = await apiClient.get<ApiResponse<Dispute>>(
      `/admin/disputes/${disputeId}`
    );
    return response.data.data;
  }

  /**
   * Resolve Dispute
   * POST /admin/disputes/resolve
   */
  async resolveDispute(data: ResolveDisputeRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/disputes/resolve',
      data
    );
    return response.data.data;
  }

  // ============== Offers & Promotions Methods ========
  /**
   * Create Offer
   * POST /admin/offers
   */
  async createOffer(data: CreateOfferRequest): Promise<{ offer_id: number }> {
    const response = await apiClient.post<ApiResponse<{ offer_id: number }>>(
      '/admin/offers',
      data
    );
    return response.data.data;
  }

  /**
   * Get All Offers
   * GET /admin/offers
   */
  async getAllOffers(params: GetOffersParams = {}): Promise<OfferListResponse> {
    const response = await apiClient.get<ApiResponse<OfferListResponse>>(
      '/admin/offers',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Offer by ID
   * GET /admin/offers/:offer_id
   */
  async getOfferById(offerId: number): Promise<Offer> {
    const response = await apiClient.get<ApiResponse<{ offer: Offer }>>(
      `/admin/offers/${offerId}`
    );
    return response.data.data.offer;
  }

  /**
   * Update Offer
   * PUT /admin/offers
   */
  async updateOffer(data: UpdateOfferRequest): Promise<Offer> {
    const response = await apiClient.put<ApiResponse<Offer>>(
      '/admin/offers',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Offer
   * DELETE /admin/offers
   */
  async deleteOffer(offerId: number): Promise<{ offer_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ offer_id: number; deleted: boolean }>>(
      '/admin/offers',
      {
        data: { offer_id: offerId }
      }
    );
    return response.data.data;
  }

  /**
   * Toggle Offer Status
   * POST /admin/offers/toggle-status
   */
  async toggleOfferStatus(data: ToggleOfferStatusRequest): Promise<Offer> {
    const response = await apiClient.post<ApiResponse<Offer>>(
      '/admin/offers/toggle-status',
      data
    );
    return response.data.data;
  }

  /**
   * Get Offer Usage Statistics
   * GET /admin/offers/usage-statistics
   */
  async getOfferUsageStatistics(offerId?: number, startDate?: string, endDate?: string): Promise<OfferUsageStatistics[]> {
    const params: any = {};
    if (offerId) params.offer_id = offerId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get<ApiResponse<OfferUsageStatistics[]>>(
      '/admin/offers/usage-statistics',
      { params }
    );
    return response.data.data;
  }

  /**
   * Associate Offer with Category
   * POST /admin/offers/associate-category
   */
  async associateOfferCategory(data: AssociateOfferCategoryRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/offers/associate-category',
      data
    );
    return response.data.data;
  }

  /**
   * Associate Offer with Business
   * POST /admin/offers/associate-business
   */
  async associateOfferBusiness(data: AssociateOfferBusinessRequest): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/offers/associate-business',
      data
    );
    return response.data.data;
  }

  /**
   * Create Coupon Code
   * POST /admin/coupons
   */
  async createCoupon(data: CreateCouponRequest): Promise<{ coupon_id: number }> {
    const response = await apiClient.post<ApiResponse<{ coupon_id: number }>>(
      '/admin/coupons',
      data
    );
    return response.data.data;
  }

  /**
   * Get All Coupon Codes
   * GET /admin/coupons
   */
  async getAllCoupons(params: GetCouponsParams = {}): Promise<CouponListResponse> {
    const response = await apiClient.get<ApiResponse<CouponListResponse>>(
      '/admin/coupons',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Coupon Code by ID
   * GET /admin/coupons/:coupon_id
   */
  async getCouponById(couponId: number): Promise<CouponDetailsResponse> {
    const response = await apiClient.get<ApiResponse<CouponDetailsResponse>>(
      `/admin/coupons/${couponId}`
    );
    return response.data.data;
  }

  /**
   * Update Coupon Code
   * PUT /admin/coupons
   */
  async updateCoupon(data: UpdateCouponRequest): Promise<Coupon> {
    const response = await apiClient.put<ApiResponse<Coupon>>(
      '/admin/coupons',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Coupon Code
   * DELETE /admin/coupons
   */
  async deleteCoupon(couponId: number): Promise<{ coupon_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ coupon_id: number; deleted: boolean }>>(
      '/admin/coupons',
      {
        data: { coupon_id: couponId }
      }
    );
    return response.data.data;
  }

  /**
   * Get Coupon Usage Statistics
   * GET /admin/coupons/usage-statistics
   */
  async getCouponUsageStatistics(couponId?: number, startDate?: string, endDate?: string): Promise<CouponUsageStatistics[]> {
    const params: any = {};
    if (couponId) params.coupon_id = couponId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get<ApiResponse<CouponUsageStatistics[]>>(
      '/admin/coupons/usage-statistics',
      { params }
    );
    return response.data.data;
  }

  // ============== Security & Audit Management Methods ========
  /**
   * Get Audit Logs
   * GET /admin/audit-logs
   */
  async getAuditLogs(params: GetAuditLogsParams = {}): Promise<AuditLogListResponse> {
    const response = await apiClient.get<ApiResponse<AuditLogListResponse>>(
      '/admin/audit-logs',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Admin Login History
   * GET /admin/login-history
   */
  async getLoginHistory(params: GetLoginHistoryParams = {}): Promise<LoginHistoryListResponse> {
    const response = await apiClient.get<ApiResponse<LoginHistoryListResponse>>(
      '/admin/login-history',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Admin Sessions
   * GET /admin/sessions
   */
  async getAdminSessions(params: GetAdminSessionsParams = {}): Promise<AdminSessionListResponse> {
    const response = await apiClient.get<ApiResponse<AdminSessionListResponse>>(
      '/admin/sessions',
      { params }
    );
    return response.data.data;
  }

  /**
   * Revoke Admin Session
   * POST /admin/sessions/revoke
   */
  async revokeAdminSession(data: RevokeSessionRequest): Promise<{ session_id: number; revoked: boolean }> {
    const response = await apiClient.post<ApiResponse<{ session_id: number; revoked: boolean }>>(
      '/admin/sessions/revoke',
      data
    );
    return response.data.data;
  }

  /**
   * Get Blocked IPs
   * GET /admin/blocked-ips
   */
  async getBlockedIPs(page: number = 1, limit: number = 20): Promise<BlockedIPListResponse> {
    const response = await apiClient.get<ApiResponse<BlockedIPListResponse>>(
      '/admin/blocked-ips',
      { params: { page, limit } }
    );
    return response.data.data;
  }

  /**
   * Block IP Address
   * POST /admin/blocked-ips/block
   */
  async blockIPAddress(data: BlockIPRequest): Promise<{ ip_address: string; reason: string; blocked: boolean; block_duration_days?: number }> {
    const response = await apiClient.post<ApiResponse<{ ip_address: string; reason: string; blocked: boolean; block_duration_days?: number }>>(
      '/admin/blocked-ips/block',
      data
    );
    return response.data.data;
  }

  /**
   * Unblock IP Address
   * POST /admin/blocked-ips/unblock
   */
  async unblockIPAddress(data: UnblockIPRequest): Promise<{ ip_address: string; unblocked: boolean }> {
    const response = await apiClient.post<ApiResponse<{ ip_address: string; unblocked: boolean }>>(
      '/admin/blocked-ips/unblock',
      data
    );
    return response.data.data;
  }

  /**
   * Get Security Statistics
   * GET /admin/security/statistics
   */
  async getSecurityStatistics(): Promise<SecurityStatistics> {
    const response = await apiClient.get<ApiResponse<SecurityStatistics>>(
      '/admin/security/statistics'
    );
    return response.data.data;
  }

  /**
   * Get Failed Login Attempts
   * GET /admin/security/failed-logins
   */
  async getFailedLoginAttempts(params: GetFailedLoginAttemptsParams = {}): Promise<FailedLoginAttemptListResponse> {
    const response = await apiClient.get<ApiResponse<FailedLoginAttemptListResponse>>(
      '/admin/security/failed-logins',
      { params }
    );
    return response.data.data;
  }

  // ============== Roles & Permissions Management Methods ========
  /**
   * Get All Roles
   * GET /admin/roles
   */
  async getAllRoles(): Promise<Role[]> {
    const response = await apiClient.get<ApiResponse<Role[]>>('/admin/roles');
    return response.data.data;
  }

  /**
   * Create Role
   * POST /admin/roles
   */
  async createRole(data: CreateRoleRequest): Promise<{ role_id: number }> {
    const response = await apiClient.post<ApiResponse<{ role_id: number }>>(
      '/admin/roles',
      data
    );
    return response.data.data;
  }

  /**
   * Update Role
   * PUT /admin/roles
   */
  async updateRole(data: UpdateRoleRequest): Promise<Role> {
    const response = await apiClient.put<ApiResponse<Role>>(
      '/admin/roles',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Role
   * DELETE /admin/roles
   */
  async deleteRole(roleId: number): Promise<{ role_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ role_id: number; deleted: boolean }>>(
      '/admin/roles',
      {
        data: { role_id: roleId }
      }
    );
    return response.data.data;
  }

  /**
   * Get All Permissions
   * GET /admin/permissions
   */
  async getAllPermissions(): Promise<Permission[]> {
    const response = await apiClient.get<ApiResponse<Permission[]>>('/admin/permissions');
    return response.data.data;
  }

  /**
   * Create Permission
   * POST /admin/permissions
   */
  async createPermission(data: CreatePermissionRequest): Promise<{ permission_id: number }> {
    const response = await apiClient.post<ApiResponse<{ permission_id: number }>>(
      '/admin/permissions',
      data
    );
    return response.data.data;
  }

  /**
   * Get Role Permissions
   * GET /admin/roles/permissions?role_id=X
   */
  async getRolePermissions(roleId: number): Promise<RolePermissionsResponse> {
    const response = await apiClient.get<ApiResponse<RolePermissionsResponse>>(
      '/admin/roles/permissions',
      { params: { role_id: roleId } }
    );
    return response.data.data;
  }

  /**
   * Assign Permissions to Role
   * POST /admin/roles/assign-permissions
   */
  async assignPermissionsToRole(data: AssignPermissionsToRoleRequest): Promise<{ role_id: number; permissions: RolePermission[] }> {
    const response = await apiClient.post<ApiResponse<{ role_id: number; permissions: RolePermission[] }>>(
      '/admin/roles/assign-permissions',
      data
    );
    return response.data.data;
  }

  /**
   * Assign Role to Admin
   * POST /admin/roles/assign-to-admin
   */
  async assignRoleToAdmin(data: AssignRoleToAdminRequest): Promise<{ target_admin_id: number; role_id: number; role_name: string }> {
    const response = await apiClient.post<ApiResponse<{ target_admin_id: number; role_id: number; role_name: string }>>(
      '/admin/roles/assign-to-admin',
      data
    );
    return response.data.data;
  }

  /**
   * Remove Role from Admin
   * POST /admin/roles/remove-from-admin
   */
  async removeRoleFromAdmin(data: RemoveRoleFromAdminRequest): Promise<{ target_admin_id: number; role_id: number; removed: boolean }> {
    const response = await apiClient.post<ApiResponse<{ target_admin_id: number; role_id: number; removed: boolean }>>(
      '/admin/roles/remove-from-admin',
      data
    );
    return response.data.data;
  }

  /**
   * Get Admin Roles
   * GET /admin/admins/roles?admin_id=X
   */
  async getAdminRoles(adminId: number): Promise<AdminRolesResponse> {
    const response = await apiClient.get<ApiResponse<AdminRolesResponse>>(
      '/admin/admins/roles',
      { params: { admin_id: adminId } }
    );
    return response.data.data;
  }

  // ============== Fraud Detection Management Methods ========
  /**
   * Get Fraud Flagged Transactions
   * GET /admin/fraud/transactions
   */
  async getFraudFlaggedTransactions(params: GetFlaggedTransactionsParams = {}): Promise<FlaggedTransactionsResponse> {
    const response = await apiClient.get<ApiResponse<FlaggedTransactionsResponse>>(
      '/admin/fraud/transactions',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Fraud Statistics
   * GET /admin/fraud/statistics
   */
  async getFraudStatistics(): Promise<FraudStatistics> {
    const response = await apiClient.get<ApiResponse<FraudStatistics>>(
      '/admin/fraud/statistics'
    );
    return response.data.data;
  }

  /**
   * Auto Detect Fraud
   * GET /admin/fraud/auto-detect
   */
  async autoDetectFraud(params: AutoDetectFraudParams = {}): Promise<AutoDetectFraudResponse> {
    const response = await apiClient.get<ApiResponse<AutoDetectFraudResponse>>(
      '/admin/fraud/auto-detect',
      { params }
    );
    return response.data.data;
  }

  /**
   * Flag Transaction
   * POST /admin/fraud/flag
   */
  async flagTransaction(data: FlagTransactionRequest): Promise<{ transaction_id: string; fraud_type: FraudType; severity: FraudSeverity; flagged: boolean }> {
    const response = await apiClient.post<ApiResponse<{ transaction_id: string; fraud_type: FraudType; severity: FraudSeverity; flagged: boolean }>>(
      '/admin/fraud/flag',
      data
    );
    return response.data.data;
  }

  /**
   * Resolve Fraud Case
   * POST /admin/fraud/resolve
   */
  async resolveFraudCase(data: ResolveFraudCaseRequest): Promise<{ transaction_id: string; resolution: FraudResolution; resolution_notes: string }> {
    const response = await apiClient.post<ApiResponse<{ transaction_id: string; resolution: FraudResolution; resolution_notes: string }>>(
      '/admin/fraud/resolve',
      data
    );
    return response.data.data;
  }

  /**
   * Freeze Entity for Fraud
   * POST /admin/fraud/freeze
   */
  async freezeForFraud(data: FreezeForFraudRequest): Promise<{ entity_type: string; entity_id: number; reason: string; duration_days?: number; frozen: boolean }> {
    const response = await apiClient.post<ApiResponse<{ entity_type: string; entity_id: number; reason: string; duration_days?: number; frozen: boolean }>>(
      '/admin/fraud/freeze',
      data
    );
    return response.data.data;
  }

  // ============== OTP Security Management Methods ========
  /**
   * Get OTP Security Rules
   * GET /admin/otp-security/rules
   */
  async getOTPSecurityRules(): Promise<OTPSecurityRule[]> {
    const response = await apiClient.get<ApiResponse<OTPSecurityRule[]>>(
      '/admin/otp-security/rules'
    );
    return response.data.data;
  }

  /**
   * Create OTP Security Rule
   * POST /admin/otp-security/rules
   */
  async createOTPSecurityRule(data: CreateOTPRuleRequest): Promise<{ rule_id: number }> {
    const response = await apiClient.post<ApiResponse<{ rule_id: number }>>(
      '/admin/otp-security/rules',
      data
    );
    return response.data.data;
  }

  /**
   * Update OTP Security Rule
   * PUT /admin/otp-security/rules
   */
  async updateOTPSecurityRule(data: UpdateOTPRuleRequest): Promise<OTPSecurityRule> {
    const response = await apiClient.put<ApiResponse<OTPSecurityRule>>(
      '/admin/otp-security/rules',
      data
    );
    return response.data.data;
  }

  /**
   * Delete OTP Security Rule
   * DELETE /admin/otp-security/rules
   */
  async deleteOTPSecurityRule(ruleId: number): Promise<{ rule_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ rule_id: number; deleted: boolean }>>(
      '/admin/otp-security/rules',
      {
        data: { rule_id: ruleId }
      }
    );
    return response.data.data;
  }

  /**
   * Get OTP Security Statistics
   * GET /admin/otp-security/statistics
   */
  async getOTPSecurityStatistics(): Promise<OTPSecurityStatistics> {
    const response = await apiClient.get<ApiResponse<OTPSecurityStatistics>>(
      '/admin/otp-security/statistics'
    );
    return response.data.data;
  }

  /**
   * Get Failed OTP Attempts
   * GET /admin/otp-security/failed-attempts
   */
  async getFailedOTPAttempts(params: GetFailedOTPAttemptsParams = {}): Promise<FailedOTPAttemptsResponse> {
    const response = await apiClient.get<ApiResponse<FailedOTPAttemptsResponse>>(
      '/admin/otp-security/failed-attempts',
      { params }
    );
    return response.data.data;
  }

  // ============== Notification Management Methods ========
  /**
   * Get Admin's Own Notifications
   * GET /admin/notifications
   */
  async getAdminNotifications(params: GetAdminNotificationsParams = {}): Promise<AdminNotificationListResponse> {
    const response = await apiClient.get<ApiResponse<AdminNotificationListResponse>>(
      '/admin/notifications',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Unread Notification Count
   * GET /admin/notifications/unread-count
   */
  async getUnreadNotificationCount(): Promise<{ count: number }> {
    const response = await apiClient.get<ApiResponse<{ count: number }>>(
      '/admin/notifications/unread-count'
    );
    return response.data.data;
  }

  /**
   * Get Notification Statistics
   * GET /admin/notifications/stats
   */
  async getNotificationStats(): Promise<NotificationStats> {
    const response = await apiClient.get<ApiResponse<NotificationStats>>(
      '/admin/notifications/stats'
    );
    return response.data.data;
  }

  /**
   * Mark All Notifications as Read
   * PUT /admin/notifications/read-all
   */
  async markAllNotificationsRead(): Promise<{ notifications_marked: number; user_id: number | null; admin_id: number }> {
    const response = await apiClient.put<ApiResponse<{ notifications_marked: number; user_id: number | null; admin_id: number }>>(
      '/admin/notifications/read-all'
    );
    return response.data.data;
  }

  /**
   * Mark Notification as Read
   * PUT /admin/notifications/:notification_id/read
   */
  async markNotificationRead(notificationId: number): Promise<{ notification_id: number; marked_as_read: boolean }> {
    const response = await apiClient.put<ApiResponse<{ notification_id: number; marked_as_read: boolean }>>(
      `/admin/notifications/${notificationId}/read`
    );
    return response.data.data;
  }

  /**
   * Delete Notification
   * DELETE /admin/notifications/:notification_id
   */
  async deleteNotification(notificationId: number): Promise<{ notification_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ notification_id: number; deleted: boolean }>>(
      `/admin/notifications/${notificationId}`
    );
    return response.data.data;
  }

  /**
   * Send Immediate Notification
   * POST /admin/send-notification
   */
  async sendNotification(data: SendNotificationRequest): Promise<{ total_sent: number; results: any[]; notification_saved: boolean }> {
    const response = await apiClient.post<ApiResponse<{ total_sent: number; results: any[]; notification_saved: boolean }>>(
      '/admin/send-notification',
      data
    );
    return response.data.data;
  }

  /**
   * Schedule Notification
   * POST /admin/schedule-notification
   */
  async scheduleNotification(data: ScheduleNotificationRequest): Promise<{ notification_id: number }> {
    const response = await apiClient.post<ApiResponse<{ notification_id: number }>>(
      '/admin/schedule-notification',
      data
    );
    return response.data.data;
  }

  /**
   * Get All Scheduled/Broadcast Notifications
   * GET /admin/notifications/admin
   */
  async getBroadcastNotifications(): Promise<AdminBroadcastNotification[]> {
    const response = await apiClient.get<ApiResponse<AdminBroadcastNotification[]>>(
      '/admin/notifications/admin'
    );
    return response.data.data;
  }

  /**
   * Update Scheduled Notification
   * POST /admin/notifications/admin/update
   */
  async updateNotification(data: UpdateNotificationRequest): Promise<{ notification_id: number; title: string; updated: boolean }> {
    const response = await apiClient.post<ApiResponse<{ notification_id: number; title: string; updated: boolean }>>(
      '/admin/notifications/admin/update',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Broadcast Notification
   * DELETE /admin/notifications/admin/:notification_id
   */
  async deleteBroadcastNotification(notificationId: number): Promise<{ notification_id: string; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ notification_id: string; deleted: boolean }>>(
      `/admin/notifications/admin/${notificationId}`
    );
    return response.data.data;
  }

  // ============== Business Documents Management ========
  /**
   * Get All Business Documents
   * GET /admin/business-documents
   */
  async getBusinessDocuments(params?: GetBusinessDocumentsParams): Promise<BusinessDocumentListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const url = `/admin/business-documents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<ApiResponse<BusinessDocumentListResponse>>(url);
    return response.data.data;
  }

  /**
   * Get Business Document by ID
   * GET /admin/business-documents/:id
   */
  async getBusinessDocumentById(id: number): Promise<BusinessDocument> {
    const response = await apiClient.get<ApiResponse<BusinessDocument>>(
      `/admin/business-documents/${id}`
    );
    return response.data.data;
  }

  /**
   * Create Business Document
   * POST /admin/business-documents/create
   */
  async createBusinessDocument(data: CreateBusinessDocumentRequest): Promise<BusinessDocument> {
    const response = await apiClient.post<ApiResponse<BusinessDocument>>(
      '/admin/business-documents/create',
      data
    );
    return response.data.data;
  }



  /**
   * Update Business Document
   * PUT /admin/business-documents/update
   */
  async updateBusinessDocument(data: UpdateBusinessDocumentRequest): Promise<BusinessDocument> {
    const response = await apiClient.put<ApiResponse<BusinessDocument>>(
      '/admin/business-documents/update',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Business Document
   * DELETE /admin/business-documents/:id
   */
  async deleteBusinessDocument(id: number): Promise<{ id: number; document_name: string; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ id: number; document_name: string; deleted: boolean }>>(
      `/admin/business-documents/${id}`
    );
    return response.data.data;
  }

  // ============== Testimonial Management Methods ========
  /**
   * Get All Testimonials
   * GET /admin/testimonials
   */
  async getAllTestimonials(params: GetTestimonialsParams = {}): Promise<TestimonialListResponse> {
    const response = await apiClient.get<ApiResponse<TestimonialListResponse>>(
      '/admin/testimonials',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Testimonial By ID
   * GET /admin/testimonials/:id
   */
  async getTestimonialById(id: number): Promise<Testimonial> {
    const response = await apiClient.get<ApiResponse<Testimonial>>(
      `/admin/testimonials/${id}`
    );
    return response.data.data;
  }

  /**
   * Create Testimonial
   * POST /admin/testimonials/create
   */
  async createTestimonial(data: CreateTestimonialRequest): Promise<Testimonial> {
    const response = await apiClient.post<ApiResponse<Testimonial>>(
      '/admin/testimonials/create',
      data
    );
    return response.data.data;
  }

  /**
   * Update Testimonial
   * POST /admin/testimonials/update
   */
  async updateTestimonial(data: UpdateTestimonialRequest): Promise<Testimonial> {
    const response = await apiClient.post<ApiResponse<Testimonial>>(
      '/admin/testimonials/update',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Testimonial
   * DELETE /admin/testimonials/:id
   */
  async deleteTestimonial(id: number): Promise<{ testimonial_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ testimonial_id: number; deleted: boolean }>>(
      `/admin/testimonials/${id}`
    );
    return response.data.data;
  }

  // ============== Blog Management Methods ========
  /**
   * Get All Blogs
   * GET /admin/blogs
   */
  async getAllBlogs(params: GetBlogsParams = {}): Promise<BlogListResponse> {
    const response = await apiClient.get<ApiResponse<BlogListResponse>>(
      '/admin/blogs',
      { params }
    );
    // Parse tags and seo_keywords if they're strings
    const data = response.data.data;
    data.blogs = data.blogs.map((blog: any) => ({
      ...blog,
      tags: typeof blog.tags === 'string' ? JSON.parse(blog.tags || '[]') : blog.tags || [],
      seo_keywords: typeof blog.seo_keywords === 'string' ? JSON.parse(blog.seo_keywords || '[]') : blog.seo_keywords || [],
    }));
    return data;
  }

  /**
   * Get Blog By ID
   * GET /admin/blogs/:id
   */
  async getBlogById(id: number): Promise<Blog> {
    const response = await apiClient.get<ApiResponse<Blog>>(
      `/admin/blogs/${id}`
    );
    // Parse tags and seo_keywords if they're strings
    const blog: any = response.data.data;
    return {
      ...blog,
      tags: typeof blog.tags === 'string' ? JSON.parse(blog.tags || '[]') : blog.tags || [],
      seo_keywords: typeof blog.seo_keywords === 'string' ? JSON.parse(blog.seo_keywords || '[]') : blog.seo_keywords || [],
    };
  }

  /**
   * Create Blog
   * POST /admin/blogs/create (form-data)
   */
  async createBlog(data: CreateBlogRequest): Promise<Blog> {
    const formData = new FormData();

    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('content', data.content);
    formData.append('category', data.category);
    formData.append('author', data.author);

    if (data.image) {
      formData.append('file', data.image);
    }

    if (data.tags && data.tags.length > 0) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    if (data.seoKeywords && data.seoKeywords.length > 0) {
      formData.append('seoKeywords', JSON.stringify(data.seoKeywords));
    }

    if (data.readTime) {
      formData.append('readTime', data.readTime.toString());
    }

    if (data.status) {
      formData.append('status', data.status);
    }

    if (data.featured !== undefined) {
      formData.append('featured', data.featured.toString());
    }

    if (data.slug) {
      formData.append('slug', data.slug);
    }

    if (data.metaTitle) {
      formData.append('metaTitle', data.metaTitle);
    }

    if (data.metaDescription) {
      formData.append('metaDescription', data.metaDescription);
    }

    if (data.publishedBy) {
      formData.append('publishedBy', data.publishedBy);
    }

    const response = await apiClient.post<ApiResponse<Blog>>(
      '/admin/blogs/create',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  /**
   * Update Blog
   * POST /admin/blogs/update (form-data)
   */
  async updateBlog(data: UpdateBlogRequest): Promise<Blog> {
    const formData = new FormData();

    formData.append('id', data.id.toString());

    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.content) formData.append('content', data.content);
    if (data.category) formData.append('category', data.category);
    if (data.author) formData.append('author', data.author);

    if (data.image !== undefined) {
      if (data.image) {
        formData.append('file', data.image);
      }
    }

    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    if (data.seoKeywords) {
      formData.append('seoKeywords', JSON.stringify(data.seoKeywords));
    }

    if (data.readTime) {
      formData.append('readTime', data.readTime.toString());
    }

    if (data.status) {
      formData.append('status', data.status);
    }

    if (data.featured !== undefined) {
      formData.append('featured', data.featured.toString());
    }

    if (data.slug) {
      formData.append('slug', data.slug);
    }

    if (data.metaTitle) {
      formData.append('metaTitle', data.metaTitle);
    }

    if (data.metaDescription) {
      formData.append('metaDescription', data.metaDescription);
    }

    if (data.publishedBy) {
      formData.append('publishedBy', data.publishedBy);
    }

    const response = await apiClient.post<ApiResponse<Blog>>(
      '/admin/blogs/update',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  /**
   * Delete Blog
   * DELETE /admin/blogs/:id
   */
  async deleteBlog(id: number): Promise<{ blog_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ blog_id: number; deleted: boolean }>>(
      `/admin/blogs/${id}`
    );
    return response.data.data;
  }

  // ============== Contact Submission Management Methods ========
  /**
   * Get All Contact Submissions
   * GET /admin/contact-submissions
   */
  async getAllContactSubmissions(params: GetContactSubmissionsParams = {}): Promise<ContactSubmissionListResponse> {
    const response = await apiClient.get<ApiResponse<ContactSubmissionListResponse>>(
      '/admin/contact-submissions',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get Contact Submission By ID
   * GET /admin/contact-submissions/:id
   */
  async getContactSubmissionById(id: number): Promise<ContactSubmission> {
    const response = await apiClient.get<ApiResponse<ContactSubmission>>(
      `/admin/contact-submissions/${id}`
    );
    const submission: any = response.data.data;
    return {
      ...submission,
      is_read: Boolean(submission.is_read),
    };
  }

  /**
   * Update Contact Submission Status
   * POST /admin/contact-submissions/update-status
   */
  async updateContactSubmissionStatus(data: UpdateContactSubmissionStatusRequest): Promise<ContactSubmission> {
    const response = await apiClient.post<ApiResponse<ContactSubmission>>(
      '/admin/contact-submissions/update-status',
      data
    );
    const submission: any = response.data.data;
    return {
      ...submission,
      is_read: Boolean(submission.is_read),
    };
  }

  /**
   * Delete Contact Submission
   * DELETE /admin/contact-submissions/:id
   */
  async deleteContactSubmission(id: number): Promise<{ submission_id: number; deleted: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ submission_id: number; deleted: boolean }>>(
      `/admin/contact-submissions/${id}`
    );
    return response.data.data;
  }

  // ============== Admin Bank Account Methods ========
  /**
   * Get All Bank Accounts
   * GET /admin/get-all-bank-accounts
   */
  async getAllBankAccounts(): Promise<AdminBankAccount[]> {
    const response = await apiClient.get<ApiResponse<AdminBankAccount[]>>(
      '/admin/get-all-bank-accounts'
    );
    return response.data.data;
  }

  /**
   * Add Bank Account
   * POST /admin/add-bank-account
   */
  async addBankAccount(data: AddAdminBankAccountRequest): Promise<AdminBankAccount> {
    const response = await apiClient.post<ApiResponse<AdminBankAccount>>(
      '/admin/add-bank-account',
      data
    );
    return response.data.data;
  }

  /**
   * Update Bank Account
   * POST /admin/update-bank-account
   */
  async updateBankAccount(data: UpdateAdminBankAccountRequest): Promise<AdminBankAccount> {
    const response = await apiClient.post<ApiResponse<AdminBankAccount>>(
      '/admin/update-bank-account',
      data
    );
    return response.data.data;
  }

  /**
   * Delete Bank Account
   * POST /admin/delete-bank-account
   */
  async deleteBankAccount(bankAccountId: number): Promise<{ bank_account_id: number }> {
    const response = await apiClient.post<ApiResponse<{ bank_account_id: number }>>(
      '/admin/delete-bank-account',
      { bank_account_id: bankAccountId }
    );
    return response.data.data;
  }

  // ============== Payment Method Management Methods ========
  /**
   * Get All Payment Methods
   * GET /admin/payment-methods
   */
  async getAllPaymentMethods(): Promise<AdminPaymentMethod[]> {
    const response = await apiClient.get<ApiResponse<AdminPaymentMethod[]>>(
      '/admin/payment-methods'
    );
    return response.data.data;
  }

  /**
   * Get Single Payment Method
   * GET /admin/payment-methods/:method_id
   */
  async getPaymentMethod(methodId: number): Promise<AdminPaymentMethod> {
    const response = await apiClient.get<ApiResponse<AdminPaymentMethod>>(
      `/admin/payment-methods/${methodId}`
    );
    return response.data.data;
  }

  /**
   * Create Payment Method
   * POST /admin/payment-methods
   */
  async createPaymentMethod(data: CreatePaymentMethodRequest): Promise<AdminPaymentMethod> {
    const response = await apiClient.post<ApiResponse<AdminPaymentMethod>>(
      '/admin/payment-methods',
      data
    );
    return response.data.data;
  }

  /**
   * Update Payment Method
   * PUT /admin/payment-methods/:method_id
   */
  async updatePaymentMethod(data: UpdatePaymentMethodRequest): Promise<AdminPaymentMethod> {
    const { method_id, ...updateData } = data;
    const response = await apiClient.put<ApiResponse<AdminPaymentMethod>>(
      `/admin/payment-methods/${method_id}`,
      updateData
    );
    return response.data.data;
  }

  /**
   * Delete Payment Method
   * DELETE /admin/payment-methods/:method_id
   */
  async deletePaymentMethod(methodId: number): Promise<{ payment_method_id: number }> {
    const response = await apiClient.delete<ApiResponse<{ payment_method_id: number }>>(
      `/admin/payment-methods/${methodId}`
    );
    return response.data.data;
  }

  /**
   * Toggle Payment Method Status
   * POST /admin/payment-methods/toggle-status
   */
  async togglePaymentMethodStatus(methodId: number, status: 'active' | 'inactive'): Promise<AdminPaymentMethod> {
    const response = await apiClient.post<ApiResponse<AdminPaymentMethod>>(
      '/admin/payment-methods/toggle-status',
      { method_id: methodId, status }
    );
    return response.data.data;
  }

  /**
   * Set Default Payment Method
   * POST /admin/payment-methods/set-default
   */
  async setDefaultPaymentMethod(methodId: number): Promise<AdminPaymentMethod> {
    const response = await apiClient.post<ApiResponse<AdminPaymentMethod>>(
      '/admin/payment-methods/set-default',
      { method_id: methodId }
    );
    return response.data.data;
  }

  /**
   * Update Payment Method Order
   * POST /admin/payment-methods/update-order
   */
  async updatePaymentMethodOrder(orders: { method_id: number; sort_order: number }[]): Promise<void> {
    await apiClient.post<ApiResponse<void>>(
      '/admin/payment-methods/update-order',
      { method_orders: orders }
    );
  }

  /**
   * Bulk Delete Businesses
   * POST /admin/businesses/bulk-delete
   */
  async bulkDeleteBusinesses(businessIds: number[]): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/businesses/bulk-delete',
      { business_ids: businessIds }
    );
    return response.data.data;
  }

  /**
   * Bulk Delete Users
   * POST /admin/users/bulk-delete
   */
  async bulkDeleteUsers(userIds: number[]): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/users/bulk-delete',
      { user_ids: userIds }
    );
    return response.data.data;
  }
}

export default new AdminService();


